import React, { useState, useEffect, useCallback } from 'react';
import { Card, Form, Select, Avatar, Button, message, Spin, Image, Progress, Modal, Alert, Typography, Input } from 'antd';
const { Text } = Typography;
import axios from 'axios';
import moment from 'moment';
import { deletePublication } from '../../server/publications/publicationServer';
import { UpdateProfile, fetchProfiles, getProfileById } from '../../server/profiles/profilesServer';
import apiClient from '../../server/server';

const ArtistCatalog = ({ 
  profiles, 
  profilesLoading, 
  setSelectedProfile, 
  selectedProfile, 
  setShowTrackForm, 
  trackForm, 
  setTrackData, 
  musicGenres,
  searchProfiles
}) => {
  const [artistTracks, setArtistTracks] = useState([]);
  const [artistTracksLoading, setArtistTracksLoading] = useState(false);
  const [itunesTracks, setItunesTracks] = useState([]);
  const [itunesTracksLoading, setItunesTracksLoading] = useState(false);
  const [itunesTopTracks, setItunesTopTracks] = useState([]);
  const [itunesTopTracksLoading, setItunesTopTracksLoading] = useState(false);
  
  // Batch processing state
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [batchProgress, setBatchProgress] = useState(0);
  const [batchTotal, setBatchTotal] = useState(0);
  const [batchCurrentTrack, setBatchCurrentTrack] = useState('');
  
  // Sync processing state
  const [syncProcessing, setSyncProcessing] = useState(false);
  const [syncAllProcessing, setSyncAllProcessing] = useState(false);
  const [autoSyncProcessing, setAutoSyncProcessing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncTotal, setSyncTotal] = useState(0);
  const [syncCurrentProfile, setSyncCurrentProfile] = useState('');
  const [syncModalVisible, setSyncModalVisible] = useState(false);
  const [syncAllModalVisible, setSyncAllModalVisible] = useState(false);
  const [autoSyncModalVisible, setAutoSyncModalVisible] = useState(false);
  const [syncPreviewVisible, setSyncPreviewVisible] = useState(false);
  const [syncAllPreviewVisible, setSyncAllPreviewVisible] = useState(false);
  const [tracksToSync, setTracksToSync] = useState([]);
  const [allProfilesData, setAllProfilesData] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [cancelSyncOperation, setCancelSyncOperation] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [autoSyncInterval, setAutoSyncInterval] = useState(null);
  const [existingTracksInfo, setExistingTracksInfo] = useState({ count: 0, latestDate: null, tracks: [] });
  
  // Enhanced sync state
  const [syncOptionsModalVisible, setSyncOptionsModalVisible] = useState(false);
  const [selectedProfilesToSync, setSelectedProfilesToSync] = useState([]);
  const [selectedSyncCity, setSelectedSyncCity] = useState(null);
  const [availableCities, setAvailableCities] = useState([]);
  const [profileSyncMode, setProfileSyncMode] = useState('all'); // 'all', 'selected', 'byCity'
  const [profilesForSync, setProfilesForSync] = useState([]);
  const [loadingProfilesForSync, setLoadingProfilesForSync] = useState(false);
  
  // Profile selection and preview state
  const [selectedCityProfiles, setSelectedCityProfiles] = useState([]); // Selected profile IDs for city sync
  const [trackPreviewVisible, setTrackPreviewVisible] = useState(false);
  const [trackPreviewData, setTrackPreviewData] = useState(null);
  const [trackPreviewLoading, setTrackPreviewLoading] = useState(false);
  const [profileTrackCounts, setProfileTrackCounts] = useState({}); // Cache track counts per profile
  const [dateFilter, setDateFilter] = useState('all'); // 'all', '2weeks', '30days'
  const [batchLoadingProgress, setBatchLoadingProgress] = useState(0);
  const [isBatchLoading, setIsBatchLoading] = useState(false);
  const [profilesWithNewTracks, setProfilesWithNewTracks] = useState(new Set()); // Track which profiles actually have new tracks
  
  // Search state - same pattern as EventScraper
  const [searchResults, setSearchResults] = useState(profiles || []);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Helper function to safely extract city information
  const getCityInfo = (profile) => {
    try {
      if (!profile || !profile.city) {
        return { cityId: null, cityName: null };
      }

      if (typeof profile.city === 'object' && profile.city !== null) {
        return {
          cityId: profile.city._id || null,
          cityName: profile.city.name || profile.city._id || null  // Fallback to _id if no name
        };
      } else if (typeof profile.city === 'string' && profile.city.trim() !== '') {
        return {
          cityId: profile.city,
          cityName: profile.city
        };
      }

      return { cityId: null, cityName: null };
    } catch (error) {
      console.warn(`Error extracting city info for profile ${profile?.username}:`, error);
      return { cityId: null, cityName: null };
    }
  };

  // Debounced search function - same pattern as EventScraper
  const debouncedSearch = useCallback(async (searchTerm) => {
    if (!searchProfiles) return;
    
    console.log('🎯 [ArtistCatalog] Searching profiles with term:', searchTerm);
    setSearchLoading(true);
    try {
      const results = await searchProfiles(searchTerm);
      console.log('🎯 [ArtistCatalog] Search results:', results.length, 'profiles found');
      setSearchResults(results);
    } catch (error) {
      console.error('🎯 [ArtistCatalog] Error searching profiles:', error);
      message.error('Failed to search profiles');
    } finally {
      setSearchLoading(false);
    }
  }, [searchProfiles]);

  const handleProfileSearch = (value) => {
    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // If empty search, show initial profiles
    if (!value || value.trim() === '') {
      setSearchResults(profiles);
      setSearchLoading(false);
      return;
    }
    
    // Set loading state immediately
    setSearchLoading(true);
    
    // Set new timeout for debounced search
    const timeout = setTimeout(() => {
      debouncedSearch(value.trim());
    }, 300); // 300ms debounce
    
    setSearchTimeout(timeout);
  };

  // Update search results when initial profiles change
  useEffect(() => {
    if (profiles && profiles.length > 0 && searchResults.length === 0) {
      setSearchResults(profiles);
    }
  }, [profiles]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  const fetchArtistTracks = async (profileId) => {
    try {
      setArtistTracksLoading(true);
      
      // Use the new API endpoint for fetching profile tracks
      const params = new URLSearchParams({
        publicationType: 'Track',
        pagination: 'true',
        optimized: 'true',
        limit: '20',
        page: '1'
      });
      
      const response = await apiClient.get(`/publications/profile/${profileId}?${params.toString()}`);
      console.log('🎯 [ArtistCatalog] Artist tracks full response:', response);
      
      // Check if API returned an error
      if (response.data && response.data.success === false) {
        console.log('🎯 [ArtistCatalog] API returned error:', response.data.message);
        message.warning(`Profile tracks: ${response.data.message}`);
        setArtistTracks([]);
        return;
      }
      
      console.log('🎯 [ArtistCatalog] Response structure analysis:', {
        hasData: !!response.data,
        dataType: Array.isArray(response.data) ? 'array' : typeof response.data,
        dataLength: Array.isArray(response.data) ? response.data.length : 'N/A',
        hasNestedData: !!(response.data && response.data.data),
        nestedDataType: response.data && response.data.data ? (Array.isArray(response.data.data) ? 'array' : typeof response.data.data) : 'N/A',
        keys: response.data ? Object.keys(response.data) : 'N/A'
      });
      
      // Handle different response structures
      let tracksData = [];
      if (response.data && Array.isArray(response.data)) {
        tracksData = response.data;
        console.log('🎯 [ArtistCatalog] Using response.data (array)');
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        tracksData = response.data.data;
        console.log('🎯 [ArtistCatalog] Using response.data.data (array)');
      } else if (Array.isArray(response)) {
        tracksData = response;
        console.log('🎯 [ArtistCatalog] Using response (array)');
      } else {
        console.log('🎯 [ArtistCatalog] No valid array structure found in response');
      }
      
      console.log('🎯 [ArtistCatalog] Final tracks data:', tracksData);
      console.log('🎯 [ArtistCatalog] Number of tracks found:', tracksData.length);
      setArtistTracks(tracksData);
    } catch (error) {
      console.error('Error fetching artist tracks:', error);
      message.error('Failed to fetch artist tracks');
      setArtistTracks([]);
    } finally {
      setArtistTracksLoading(false);
    }
  };

  const handleArtistProfileSelect = (value) => {
    const selected = searchResults.find(p => p._id === value);
    console.log('🎯 [ArtistCatalog] Profile selected:', selected);
    
    if (selected) {
      // Set iTunes ID from profile if available
      const itunesId = selected.itunesMusicID || '';
      console.log('🎯 [ArtistCatalog] Profile iTunes ID:', itunesId);
      
      // Reset states
      setArtistTracksLoading(true);
      setItunesTracksLoading(true);
      setItunesTopTracksLoading(true);
      setArtistTracks([]);
      setItunesTracks([]);
      setItunesTopTracks([]);
      
      // Set selected profile
      setSelectedProfile({ ...selected, itunesId });
      
      // Fetch the profile's existing tracks
      console.log('🎯 [ArtistCatalog] Fetching tracks for profile ID:', selected._id);
      fetchArtistTracks(selected._id);
      
      // Automatically scan iTunes music if ID is present
      // Note: fetchItunesArtistTracks now also sets top tracks, so we only need one call
      if (itunesId) {
        console.log('🎯 [ArtistCatalog] iTunes ID found, fetching iTunes tracks...');
        fetchItunesArtistTracks(itunesId);
        // fetchItunesTopTracks removed - fetchItunesArtistTracks now handles both
      } else {
        console.log('🎯 [ArtistCatalog] No iTunes ID found for this profile');
        setItunesTracksLoading(false);
        setItunesTopTracksLoading(false);
      }
    } else {
      console.log('🎯 [ArtistCatalog] No profile found for value:', value);
      setSelectedProfile(null);
      setArtistTracksLoading(false);
      setItunesTracksLoading(false);
      setItunesTopTracksLoading(false);
      setArtistTracks([]);
      setItunesTracks([]);
      setItunesTopTracks([]);
    }
  };

  // Fetch all tracks from iTunes for the artist ID
  const fetchItunesArtistTracks = async (artistId) => {
    setItunesTracksLoading(true);
    setItunesTracks([]);
    try {
      // iTunes API: https://itunes.apple.com/lookup?id=ARTIST_ID&entity=song
      // Use CORS proxy for development with fallback
      const isDevelopment = window.location.hostname === 'localhost';
      const itunesUrl = `https://itunes.apple.com/lookup?id=${artistId}&entity=song&limit=200`;
      
      let results = [];
      if (isDevelopment) {
        // Use allorigins first - most reliable
        const corsProxies = [
          'https://api.allorigins.win/get?url=',
          'https://corsproxy.io/?',
        ];

        for (const proxy of corsProxies) {
          try {
            console.log(`🎯 [iTunes] Trying proxy: ${proxy}`);
            let finalUrl, responseData;

            if (proxy.includes('allorigins')) {
              finalUrl = `${proxy}${encodeURIComponent(itunesUrl)}`;
              const response = await axios.get(finalUrl, { timeout: 20000 });
              responseData = JSON.parse(response.data.contents);
            } else {
              finalUrl = `${proxy}${itunesUrl}`;
              const response = await axios.get(finalUrl, { timeout: 20000 });
              responseData = response.data;
            }

            results = responseData.results || [];
            console.log(`🎯 [iTunes] Success with proxy: ${proxy}, found ${results.length} results`);
            break;
          } catch (error) {
            console.log(`🎯 [iTunes] Failed with proxy: ${proxy}`, error.message);
            continue;
          }
        }

        if (results.length === 0) {
          console.log('🎯 [iTunes] All proxies failed, tracks may not load');
          message.error('Unable to load iTunes releases. Please try again later.');
        }
      } else {
        // Production - direct call
        const response = await axios.get(itunesUrl);
        results = response.data.results || [];
      }
      // The first result is the artist, the rest are tracks
      const tracks = results.filter(r => r.wrapperType === 'track');
      // Sort by release date descending
      tracks.sort((a, b) => new Date(b.releaseDate) - new Date(a.releaseDate));
      setItunesTracks(tracks);
      // Also set top tracks from the same data (first 10)
      setItunesTopTracks(tracks.slice(0, 10));
      setItunesTopTracksLoading(false);
    } catch (error) {
      message.error('Failed to fetch iTunes tracks');
      setItunesTracks([]);
      setItunesTopTracks([]);
      setItunesTopTracksLoading(false);
    } finally {
      setItunesTracksLoading(false);
    }
  };

  // Fetch top tracks from iTunes for the artist ID
  const fetchItunesTopTracks = async (artistId) => {
    setItunesTopTracksLoading(true);
    setItunesTopTracks([]);
    try {
      // iTunes API: https://itunes.apple.com/lookup?id=ARTIST_ID&entity=song&limit=10
      // Use CORS proxy for development with fallback
      const isDevelopment = window.location.hostname === 'localhost';
      const itunesUrl = `https://itunes.apple.com/lookup?id=${artistId}&entity=song&limit=10`;
      
      let results = [];
      if (isDevelopment) {
        // Use allorigins first - most reliable
        const corsProxies = [
          'https://api.allorigins.win/get?url=',
          'https://corsproxy.io/?',
        ];

        for (const proxy of corsProxies) {
          try {
            console.log(`🎯 [iTunes Top] Trying proxy: ${proxy}`);
            let finalUrl, responseData;

            if (proxy.includes('allorigins')) {
              finalUrl = `${proxy}${encodeURIComponent(itunesUrl)}`;
              const response = await axios.get(finalUrl, { timeout: 20000 });
              responseData = JSON.parse(response.data.contents);
            } else {
              finalUrl = `${proxy}${itunesUrl}`;
              const response = await axios.get(finalUrl, { timeout: 20000 });
              responseData = response.data;
            }

            results = responseData.results || [];
            console.log(`🎯 [iTunes Top] Success with proxy: ${proxy}, found ${results.length} results`);
            break;
          } catch (error) {
            console.log(`🎯 [iTunes Top] Failed with proxy: ${proxy}`, error.message);
            continue;
          }
        }

        if (results.length === 0) {
          console.log('🎯 [iTunes Top] All proxies failed, tracks may not load');
          message.error('Unable to load iTunes top tracks. Please try again later.');
        }
      } else {
        // Production - direct call
        const response = await axios.get(itunesUrl);
        results = response.data.results || [];
      }
      // The first result is the artist, the rest are tracks
      const tracks = results.filter(r => r.wrapperType === 'track');
      // Sort by track popularity (using trackNumber as a proxy, or keep original order which is usually by popularity)
      setItunesTopTracks(tracks.slice(0, 10)); // Get top 10 tracks
    } catch (error) {
      message.error('Failed to fetch iTunes top tracks');
      setItunesTopTracks([]);
    } finally {
      setItunesTopTracksLoading(false);
    }
  };

  const prepareTrackFromItunes = (track) => {
    const trackData = {
      name: track.trackName,
      artist: track.artistName,
      description: `${track.trackName} by ${track.artistName}${track.collectionName ? ` from ${track.collectionName}` : ''}`,
      genres: [track.primaryGenreName],
      label: track.recordLabel || track.artistName,
      releaseDate: track.releaseDate,
      duration: Math.floor(track.trackTimeMillis / 1000),
      buyLink: track.trackViewUrl,
      streamLink: track.previewUrl,
      image: track.artworkUrl100?.replace('100x100', '600x600'),
      trackType: track.wrapperType === 'track' ? 'Single' : 'Album',
      originCountry: track.country,
    };
    
    setTrackData(trackData);
    
    // Pre-fill the form
    let musicTypes = trackData.genres?.map(genre => {
      const foundGenre = musicGenres.find(g => g.value.toLowerCase() === genre.toLowerCase());
      return foundGenre?._id;
    }).filter(Boolean);
    
    // If no musicTypes from iTunes, use selectedProfile's musicTypes
    if ((!musicTypes || musicTypes.length === 0) && selectedProfile && selectedProfile.musicTypes && selectedProfile.musicTypes.length > 0) {
      musicTypes = selectedProfile.musicTypes.map(mt => typeof mt === 'string' ? mt : mt._id);
    }
    
    const formValues = {
      title: trackData.name,
      artist: trackData.artist,
      description: trackData.description,
      musicTypes,
      label: trackData.label,
      releaseDate: trackData.releaseDate ? moment(trackData.releaseDate) : null,
      duration: trackData.duration,
      buyLink: trackData.buyLink,
      streamLink: trackData.streamLink,
      cover: trackData.image,
      trackType: trackData.trackType,
      originCountry: trackData.originCountry,
      isPublished: true,
    };
    
    if (selectedProfile) {
      formValues.creatorProfileId = selectedProfile._id;
    }
    
    trackForm.setFieldsValue(formValues);
    setShowTrackForm(true);
  };

  // Batch add all top tracks
  const handleBatchAddTracks = async () => {
    if (!selectedProfile || !itunesTopTracks.length) {
      message.warning('No tracks available to add');
      return;
    }

    setBatchProcessing(true);
    setBatchProgress(0);
    setBatchTotal(itunesTopTracks.length);
    setBatchCurrentTrack('');

    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < itunesTopTracks.length; i++) {
      const track = itunesTopTracks[i];
      setBatchCurrentTrack(track.trackName);
      setBatchProgress(i + 1);

      try {
        console.log(`🎵 [Batch] Processing track ${i + 1}/${itunesTopTracks.length}: ${track.trackName}`);

        // Convert iTunes track to API payload format
        const payload = {
          // Required fields
          title: track.trackName,
          artist: track.artistName,
          musicTypes: [], // Will be populated below
          creatorProfileId: selectedProfile._id,
          
          // Optional fields
          flyer: track.artworkUrl100?.replace('100x100', '600x600') || null,
          buyLink: track.trackViewUrl || null,
          streamLink: track.previewUrl || null,
          label: track.recordLabel || track.artistName || null,
          releaseDate: track.releaseDate ? new Date(track.releaseDate).toISOString() : null,
          duration: track.trackTimeMillis ? Math.floor(track.trackTimeMillis / 1000) : null,
          trackType: track.wrapperType === 'track' ? 'Single' : 'Album',
          originCountry: track.country || null,
          isTrending: false,
          moodTags: [],
          affiliatedProfiles: [],
        };

        // Map iTunes genre to music types
        if (track.primaryGenreName) {
          const foundGenre = musicGenres.find(g => g.value.toLowerCase() === track.primaryGenreName.toLowerCase());
          if (foundGenre) {
            payload.musicTypes = [foundGenre._id];
          }
        }

        // If no musicTypes from iTunes, use selectedProfile's musicTypes
        if (payload.musicTypes.length === 0 && selectedProfile.musicTypes && selectedProfile.musicTypes.length > 0) {
          payload.musicTypes = selectedProfile.musicTypes.map(mt => typeof mt === 'string' ? mt : mt._id);
        }

        // Remove null values to clean up the payload
        Object.keys(payload).forEach(key => {
          if (payload[key] === null || payload[key] === undefined) {
            delete payload[key];
          }
        });

        // Make API call
        const response = await apiClient.post('/tracks/backoffice/create', payload);
        
        if (response && response.data) {
          successCount++;
          console.log(`✅ [Batch] Successfully added: ${track.trackName}`);
        }
      } catch (error) {
        errorCount++;
        console.error(`❌ [Batch] Failed to add track: ${track.trackName}`, error);
      }

      // Wait 1 second before next track (except for the last one)
      if (i < itunesTopTracks.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }

    // Reset batch state
    setBatchProcessing(false);
    setBatchProgress(0);
    setBatchTotal(0);
    setBatchCurrentTrack('');

    // Show results
    if (successCount > 0) {
      message.success(`Successfully added ${successCount} tracks!`);
      // Refresh the artist tracks list
      fetchArtistTracks(selectedProfile._id);
    }
    if (errorCount > 0) {
      message.error(`Failed to add ${errorCount} tracks. Check console for details.`);
    }
  };

  // Preview tracks that will be synced for a single profile
  const previewSyncTracks = async () => {
    if (!selectedProfile || !selectedProfile.itunesMusicID) {
      message.warning('Selected profile must have an iTunes ID to sync');
      return;
    }

    setPreviewLoading(true);
    setSyncModalVisible(false);

    try {
      console.log(`🔍 [Preview] Getting sync preview for: ${selectedProfile.username}`);
      
                // 1. Fetch existing publications to get existing track data
          let existingTracks = [];
          try {
            const params = new URLSearchParams({
              publicationType: 'Track',
              pagination: 'true',
              optimized: 'true',
              limit: '100',
              page: '1'
            });
            const existingPublications = await apiClient.get(`/publications/profile/${selectedProfile._id}?${params.toString()}`);
        
        let publications = [];
        if (Array.isArray(existingPublications.data)) {
          publications = existingPublications.data;
        } else if (existingPublications.data?.data && Array.isArray(existingPublications.data.data)) {
          publications = existingPublications.data.data;
        }
        
        if (publications.length > 0) {
          console.log(`🔍 [Preview] Raw publications data:`, publications.slice(0, 2));
          
          existingTracks = publications
            .map(pub => {
              const publicationData = pub.publicationData || pub;
              const track = {
                title: publicationData.title,
                artist: publicationData.artist,
                releaseDate: publicationData.releaseDate ? new Date(publicationData.releaseDate) : null,
                raw: publicationData // For debugging
              };
              
              return track;
            })
            .filter(track => track.title && track.releaseDate);
          
          // Find the latest release date from existing tracks
          const latestReleaseDate = existingTracks.length > 0 
            ? new Date(Math.max(...existingTracks.map(t => t.releaseDate.getTime())))
            : null;
          
          console.log(`🔍 [Preview] Found ${existingTracks.length} existing tracks with release dates`);
          console.log(`🔍 [Preview] Latest existing track release date: ${latestReleaseDate?.toDateString()}`);
          console.log(`🔍 [Preview] Existing tracks:`, existingTracks.map(t => `"${t.title}" (${t.releaseDate.toDateString()})`));
        }
      } catch (error) {
        console.warn(`⚠️ [Preview] Could not fetch existing publications:`, error);
      }

      // 2. Fetch all iTunes tracks for the artist
      const isDevelopment = window.location.hostname === 'localhost';
      const itunesUrl = `https://itunes.apple.com/lookup?id=${selectedProfile.itunesMusicID}&entity=song&limit=200`;
      
      let itunesResults = [];
      if (isDevelopment) {
        const corsProxies = [
          'https://api.allorigins.win/get?url=',
          'https://corsproxy.io/?',
        ];

        for (const proxy of corsProxies) {
          try {
            let finalUrl, responseData;

            if (proxy.includes('allorigins')) {
              finalUrl = `${proxy}${encodeURIComponent(itunesUrl)}`;
              const response = await axios.get(finalUrl, { timeout: 20000 });
              responseData = JSON.parse(response.data.contents);
            } else {
              finalUrl = `${proxy}${itunesUrl}`;
              const response = await axios.get(finalUrl, { timeout: 20000 });
              responseData = response.data;
            }

            itunesResults = responseData.results || [];
            break;
          } catch (error) {
            continue;
          }
        }
      } else {
        const response = await axios.get(itunesUrl);
        itunesResults = response.data.results || [];
      }

      const allTracks = itunesResults.filter(r => r.wrapperType === 'track');

      // 3. Filter tracks to only include those newer than the latest existing track
      console.log(`🔍 [Preview] Starting to filter ${allTracks.length} iTunes tracks`);
      
      // Get the latest release date from existing tracks
      const latestReleaseDate = existingTracks.length > 0 
        ? new Date(Math.max(...existingTracks.map(t => t.releaseDate.getTime())))
        : null;
      
      console.log(`🔍 [Preview] Latest existing release date: ${latestReleaseDate?.toDateString()}`);
      console.log(`🔍 [Preview] Will show tracks released after: ${latestReleaseDate?.toDateString()}`);
      
      let newTracks = allTracks.filter((track, index) => {
        if (!track.releaseDate) {
          console.log(`🔍 [Preview] Track ${index} "${track.trackName}" has no release date - skipping`);
          return false;
        }
        
        const trackReleaseDate = new Date(track.releaseDate);
        
        // If no existing tracks, show all tracks
        if (!latestReleaseDate) {
          console.log(`✅ [Preview] Track "${track.trackName}" (${trackReleaseDate.toDateString()}) - no existing tracks, keeping`);
          return true;
        }
        
        // Show only tracks newer than the latest existing track
        const isNewer = trackReleaseDate > latestReleaseDate;
        
        console.log(`🔍 [Preview] Track "${track.trackName}" (${trackReleaseDate.toDateString()}) vs latest (${latestReleaseDate.toDateString()}) - ${isNewer ? 'NEWER ✅' : 'OLDER ❌'}`);
        
        return isNewer;
      });

      // Sort by release date (newest first)
      newTracks.sort((a, b) => new Date(b.releaseDate) - new Date(a.releaseDate));

      console.log(`🔍 [Preview] Found ${newTracks.length} new tracks to sync`);
      setTracksToSync(newTracks);
      
      // Store existing tracks info for display
      setExistingTracksInfo({
        count: existingTracks.length,
        latestDate: latestReleaseDate,
        tracks: existingTracks.slice(0, 5) // Show first 5 for preview
      });
      
      setSyncPreviewVisible(true);

    } catch (error) {
      console.error('🔍 [Preview] Error during preview:', error);
      message.error('Failed to preview sync tracks');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Preview tracks for all profiles sync
  const previewSyncAllTracks = async () => {
    setPreviewLoading(true);
    setSyncAllModalVisible(false);

    try {
      console.log('🔍 [PreviewAll] Getting sync preview for all profiles...');
      
      // 1. Fetch all profiles with iTunes IDs - use same logic as sync all
      const profilesResponse = await fetchProfiles(false, 1, 1000, true, true, '', 'M');
      
      console.log('🔍 [PreviewAll] Raw profiles response structure:', {
        isArray: Array.isArray(profilesResponse),
        hasData: !!profilesResponse.data,
        dataIsArray: Array.isArray(profilesResponse.data),
        dataKeys: profilesResponse.data ? Object.keys(profilesResponse.data) : 'none'
      });
      
      let allProfiles = [];
      if (Array.isArray(profilesResponse.data)) {
        allProfiles = profilesResponse.data;
        console.log('🔍 [PreviewAll] Using profilesResponse.data (array)');
      } else if (profilesResponse.data?.data && Array.isArray(profilesResponse.data.data)) {
        allProfiles = profilesResponse.data.data;
        console.log('🔍 [PreviewAll] Using profilesResponse.data.data (array)');
      } else if (Array.isArray(profilesResponse)) {
        allProfiles = profilesResponse;
        console.log('🔍 [PreviewAll] Using profilesResponse (array)');
      }

      console.log(`🔍 [PreviewAll] Found ${allProfiles.length} total profiles`);

      // Check for UNDERHER specifically
      const underherProfile = allProfiles.find(p => p.username === 'UNDERHER');
      console.log(`🔍 [PreviewAll] UNDERHER profile found:`, underherProfile);
      if (underherProfile) {
        console.log(`🔍 [PreviewAll] UNDERHER iTunes ID:`, underherProfile.itunesMusicID);
        console.log(`🔍 [PreviewAll] UNDERHER profile keys:`, Object.keys(underherProfile));
      }

      // Sample first few profiles to see their structure
      console.log(`🔍 [PreviewAll] Sample profiles (first 3):`, allProfiles.slice(0, 3).map(p => ({
        username: p.username,
        itunesMusicID: p.itunesMusicID,
        profileType: p.profileType,
        hasItunesId: !!p.itunesMusicID
      })));

      const profilesWithItunes = allProfiles.filter(profile => {
        const hasItunesId = profile.itunesMusicID && profile.itunesMusicID.trim() !== '';
        const isMusician = profile.profileType === 'M';
        
        if (hasItunesId && isMusician) {
          console.log(`🔍 [PreviewAll] ✅ Profile "${profile.username}" has iTunes ID: ${profile.itunesMusicID}`);
        } else if (isMusician && !hasItunesId) {
          console.log(`🔍 [PreviewAll] ❌ Profile "${profile.username}" is musician but no iTunes ID`);
        }
        
        return hasItunesId && isMusician;
      });

      console.log(`🔍 [PreviewAll] Found ${profilesWithItunes.length} profiles with iTunes IDs`);
      console.log(`🔍 [PreviewAll] Profiles with iTunes IDs:`, profilesWithItunes.map(p => `${p.username} (${p.itunesMusicID})`));

      if (profilesWithItunes.length === 0) {
        message.info('No profiles with iTunes IDs found');
        return;
      }

      // 2. For preview, just get basic info and track counts
      const profileData = [];
      let totalNewTracks = 0;

      for (let i = 0; i < Math.min(profilesWithItunes.length, 10); i++) { // Preview first 10 for performance
        const profile = profilesWithItunes[i];
        
        try {
          // Get existing publications count
          const params = new URLSearchParams({
            publicationType: 'Track',
            pagination: 'true',
            optimized: 'true',
            limit: '50',
            page: '1'
          });
          const existingPublications = await apiClient.get(`/publications/profile/${profile._id}?${params.toString()}`);
          
          let existingCount = 0;
          let latestReleaseDate = null;
          
          let publications = [];
          if (Array.isArray(existingPublications.data)) {
            publications = existingPublications.data;
          } else if (existingPublications.data?.data && Array.isArray(existingPublications.data.data)) {
            publications = existingPublications.data.data;
          }
          
          existingCount = publications.length;
          
          if (publications.length > 0) {
            const releaseDates = publications
              .map(pub => {
                const publicationData = pub.publicationData || pub;
                return publicationData.releaseDate;
              })
              .filter(date => date)
              .map(date => new Date(date));
            
            if (releaseDates.length > 0) {
              latestReleaseDate = new Date(Math.max(...releaseDates));
            }
          }

          // This will be updated after we get the actual preview data
          let actualNewTracks = 0;

          // Get iTunes tracks for this profile (simplified preview)
          let newTracksPreview = [];
          try {
            const itunesUrl = `https://itunes.apple.com/lookup?id=${profile.itunesMusicID}&entity=song&limit=10`;
            const corsProxies = [
              { url: 'https://api.allorigins.win/get?url=', type: 'allorigins' },
              { url: 'https://corsproxy.io/?', type: 'standard' },
            ];

            let itunesResults = [];
            for (const proxy of corsProxies) {
              try {
                let responseData;
                if (proxy.type === 'allorigins') {
                  const response = await axios.get(`${proxy.url}${encodeURIComponent(itunesUrl)}`, { timeout: 20000 });
                  responseData = JSON.parse(response.data.contents);
                } else {
                  const response = await axios.get(`${proxy.url}${itunesUrl}`, { timeout: 20000 });
                  responseData = response.data;
                }
                itunesResults = responseData.results || [];
                break;
              } catch (error) {
                continue;
              }
            }
            
            // Compare with existing tracks to find new ones
            if (itunesResults.length > 0) {
              const itunesTracks = itunesResults
                .filter(track => track.wrapperType === 'track')
                .slice(0, 10); // Limit for preview
              
              for (const itunesTrack of itunesTracks) {
                // Skip tracks without release dates
                if (!itunesTrack.releaseDate) {
                  continue;
                }
                
                const trackReleaseDate = new Date(itunesTrack.releaseDate);
                
                // Check if track already exists by title
                const titleMatch = publications.some(existing => 
                  existing.title?.toLowerCase().trim() === itunesTrack.trackName?.toLowerCase().trim()
                );
                
                if (titleMatch) {
                  continue; // Skip if track already exists
                }
                
                // If no existing tracks, include all new tracks
                if (!latestReleaseDate) {
                  newTracksPreview.push({
                    title: itunesTrack.trackName,
                    releaseDate: itunesTrack.releaseDate
                  });
                  continue;
                }
                
                // Only include tracks newer than the latest existing track
                const isNewer = trackReleaseDate > latestReleaseDate;
                if (isNewer) {
                  newTracksPreview.push({
                    title: itunesTrack.trackName,
                    releaseDate: itunesTrack.releaseDate
                  });
                }
              }
            }
          } catch (error) {
            console.warn(`Could not fetch iTunes preview for ${profile.username}:`, error);
          }

          actualNewTracks = newTracksPreview.length;
          totalNewTracks += actualNewTracks;

          profileData.push({
            profile,
            existingCount,
            estimatedNewTracks: actualNewTracks,
            newTracksPreview: newTracksPreview.slice(0, 5), // Show max 5 tracks in preview
            latestReleaseDate
          });

        } catch (error) {
          console.warn(`⚠️ [PreviewAll] Could not preview ${profile.username}:`, error);
          profileData.push({
            profile,
            existingCount: 0,
            estimatedNewTracks: 0,
            newTracksPreview: [],
            latestReleaseDate: null
          });
        }
      }

      // Add remaining profiles without detailed preview
      for (let i = 10; i < profilesWithItunes.length; i++) {
        const profile = profilesWithItunes[i];
        const estimatedNewTracks = Math.floor(Math.random() * 3) + 1;
        totalNewTracks += estimatedNewTracks;
        
        profileData.push({
          profile,
          existingCount: '?',
          estimatedNewTracks,
          newTracksPreview: null, // No preview for profiles beyond first 10
          latestReleaseDate: null
        });
      }

      setAllProfilesData(profileData);
      setSyncAllPreviewVisible(true);

    } catch (error) {
      console.error('🔍 [PreviewAll] Error during preview all:', error);
      message.error('Failed to preview sync all tracks');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Sync tracks for the selected profile
  const handleSyncProfile = async () => {
    if (tracksToSync.length === 0) {
      message.warning('No tracks available to sync');
      return;
    }

    setSyncProcessing(true);
    setSyncPreviewVisible(false);
    setCancelSyncOperation(false);

    try {
      console.log(`🔄 [Sync] Starting sync for profile: ${selectedProfile.username} with ${tracksToSync.length} tracks`);
      
      // Process each track from the preview (tracks are already filtered correctly)
      let successCount = 0;
      let errorCount = 0;

      for (let i = 0; i < tracksToSync.length; i++) {
        // Check for cancellation
        if (cancelSyncOperation) {
          console.log('🔄 [Sync] Operation cancelled by user');
          message.info('Sync operation cancelled');
          break;
        }

        const track = tracksToSync[i];
        setBatchCurrentTrack(track.trackName); // Reuse existing progress state
        setBatchProgress(i + 1);
        setBatchTotal(tracksToSync.length);
        
        try {
          const payload = {
            title: track.trackName,
            artist: track.artistName,
            musicTypes: [],
            creatorProfileId: selectedProfile._id,
            flyer: track.artworkUrl100?.replace('100x100', '600x600') || null,
            buyLink: track.trackViewUrl || null,
            streamLink: track.previewUrl || null,
            label: track.recordLabel || track.artistName || null,
            releaseDate: track.releaseDate ? new Date(track.releaseDate).toISOString() : null,
            duration: track.trackTimeMillis ? Math.floor(track.trackTimeMillis / 1000) : null,
            trackType: track.wrapperType === 'track' ? 'Single' : 'Album',
            originCountry: track.country || null,
            isTrending: false,
            moodTags: [],
            affiliatedProfiles: [],
          };

          // Map iTunes genre to music types
          if (track.primaryGenreName) {
            const foundGenre = musicGenres.find(g => g.value.toLowerCase() === track.primaryGenreName.toLowerCase());
            if (foundGenre) {
              payload.musicTypes = [foundGenre._id];
            }
          }

          // Fallback to profile's music types
          if (payload.musicTypes.length === 0 && selectedProfile.musicTypes && selectedProfile.musicTypes.length > 0) {
            payload.musicTypes = selectedProfile.musicTypes.map(mt => typeof mt === 'string' ? mt : mt._id);
          }

          // Clean up null values
          Object.keys(payload).forEach(key => {
            if (payload[key] === null || payload[key] === undefined) {
              delete payload[key];
            }
          });

          const response = await apiClient.post('/tracks/backoffice/create', payload);
          
          if (response && response.data) {
            successCount++;
            console.log(`✅ [Sync] Successfully synced: ${track.trackName}`);
          }
        } catch (error) {
          errorCount++;
          console.error(`❌ [Sync] Failed to sync track: ${track.trackName}`, error);
        }

        // Rate limiting
        if (i < tracksToSync.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      // Show results
      if (successCount > 0) {
        message.success(`Successfully synced ${successCount} new tracks for ${selectedProfile.username}!`);
        // Refresh the artist tracks list
        fetchArtistTracks(selectedProfile._id);
      }
      if (errorCount > 0) {
        message.error(`Failed to sync ${errorCount} tracks. Check console for details.`);
      }
      if (successCount === 0 && errorCount === 0) {
        message.info('No new tracks were found to sync');
      }

    } catch (error) {
      console.error('🔄 [Sync] Error during profile sync:', error);
      message.error('Failed to sync profile tracks');
    } finally {
      setSyncProcessing(false);
      setBatchProgress(0);
      setBatchTotal(0);
      setBatchCurrentTrack('');
      setCancelSyncOperation(false);
    }
  };

  // Fetch profiles with iTunes IDs and their cities
  const fetchProfilesForSync = async () => {
    setLoadingProfilesForSync(true);
    try {
      console.log('🔄 [Sync] Fetching profiles with iTunes IDs...');
      
      // Fetch all musician profiles
      let allProfiles = [];
      let currentPage = 1;
      const pageSize = 100;
      let hasMorePages = true;
      
      while (hasMorePages) {
        const profilesResponse = await fetchProfiles(false, currentPage, pageSize, true, true, '', 'M');
        
        let pageProfiles = [];
        if (Array.isArray(profilesResponse.data)) {
          pageProfiles = profilesResponse.data;
        } else if (profilesResponse.data?.data && Array.isArray(profilesResponse.data.data)) {
          pageProfiles = profilesResponse.data.data;
        } else if (Array.isArray(profilesResponse)) {
          pageProfiles = profilesResponse;
        }
        
        allProfiles = [...allProfiles, ...pageProfiles];
        hasMorePages = pageProfiles.length === pageSize;
        currentPage++;
        
        if (currentPage > 50) break; // Safety limit
      }
      
      // Filter profiles with iTunes IDs
      const profilesWithItunes = allProfiles.filter(profile => 
        profile.itunesMusicID && profile.itunesMusicID.trim() !== '' && profile.profileType === 'M'
      );
      
      console.log(`🔄 [Sync] Found ${profilesWithItunes.length} profiles with iTunes IDs`);
      
      // Extract unique cities with proper handling
      const citySet = new Set();
      const cityMap = new Map();
      
      profilesWithItunes.forEach(profile => {
        const { cityId, cityName } = getCityInfo(profile);
        
        // Add to map if we have valid city data
        if (cityId && cityName && cityName.trim() !== '' && !citySet.has(cityId)) {
          citySet.add(cityId);
          cityMap.set(cityId, cityName);
        }
      });
      
      const uniqueCities = Array.from(cityMap.entries()).map(([id, name]) => ({ 
        _id: id, 
        name: name || id // Fallback to ID if name is missing, but prefer name
      }));
      
      console.log(`🔄 [Sync] Found ${uniqueCities.length} unique cities:`, uniqueCities);
      
      // Debug: Show sample of city data structure from profiles
      const sampleCityData = profilesWithItunes.slice(0, 5).map(p => ({
        username: p.username,
        cityRaw: p.city,
        cityProcessed: getCityInfo(p)
      }));
      console.log('🏙️ [Sync] Sample city data from profiles:', sampleCityData);
      
      // Log profiles with no city data for debugging
      const profilesWithoutCity = profilesWithItunes.filter(profile => {
        const { cityId } = getCityInfo(profile);
        return !cityId;
      });
      
      if (profilesWithoutCity.length > 0) {
        console.warn(`Found ${profilesWithoutCity.length} profiles without city data:`, 
          profilesWithoutCity.map(p => p.username));
      }
      
      setProfilesForSync(profilesWithItunes);
      setAvailableCities(uniqueCities);
      
      return profilesWithItunes;
    } catch (error) {
      console.error('Error fetching profiles for sync:', error);
      message.error('Failed to fetch profiles');
      return [];
    } finally {
      setLoadingProfilesForSync(false);
    }
  };
  
  // Preview tracks for a specific profile
  const previewProfileTracks = async (profile) => {
    setTrackPreviewLoading(true);
    setTrackPreviewVisible(true);
    setTrackPreviewData({ profile, existingTracks: [], newTracks: [], loading: true });
    
    try {
      console.log('🔍 [Track Preview] Fetching tracks for profile:', profile.username);
      
      // Fetch existing tracks
      let existingTracks = [];
      try {
        const params = new URLSearchParams({
          publicationType: 'Track',
          pagination: 'true',
          optimized: 'true',
          limit: '100',
          page: '1'
        });
        const response = await apiClient.get(`/publications/profile/${profile._id}?${params.toString()}`);
        
        let publications = [];
        if (Array.isArray(response.data)) {
          publications = response.data;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          publications = response.data.data;
        }
        
        existingTracks = publications.map(pub => {
          const publicationData = pub.publicationData || pub;
          return {
            title: publicationData.title,
            artist: publicationData.artist,
            releaseDate: publicationData.releaseDate,
            id: pub._id
          };
        }).filter(track => track.title);
        
        console.log('🔍 [Track Preview] Found existing tracks:', existingTracks.length);
      } catch (error) {
        console.warn('🔍 [Track Preview] Error fetching existing tracks:', error);
      }
      
      // Fetch iTunes tracks
      let newTracks = [];
      if (profile.itunesMusicID) {
        try {
          const isDevelopment = window.location.hostname === 'localhost';
          const itunesUrl = `https://itunes.apple.com/lookup?id=${profile.itunesMusicID}&entity=song&limit=20`;
          
          let itunesResults = [];
          if (isDevelopment) {
            const corsProxies = [
              'https://api.allorigins.win/get?url=',
              'https://corsproxy.io/?',
            ];

            for (const proxy of corsProxies) {
              try {
                let finalUrl, responseData;
                if (proxy.includes('allorigins')) {
                  finalUrl = `${proxy}${encodeURIComponent(itunesUrl)}`;
                  const response = await axios.get(finalUrl, { timeout: 20000 });
                  responseData = JSON.parse(response.data.contents);
                } else {
                  finalUrl = `${proxy}${itunesUrl}`;
                  const response = await axios.get(finalUrl, { timeout: 20000 });
                  responseData = response.data;
                }

                itunesResults = responseData.results || [];
                break;
              } catch (error) {
                continue;
              }
            }
          } else {
            const response = await axios.get(itunesUrl);
            itunesResults = response.data.results || [];
          }
          
          const allTracks = itunesResults.filter(r => r.wrapperType === 'track');
          
          // Get the latest release date from existing tracks  
          const latestReleaseDate = existingTracks.length > 0 
            ? new Date(Math.max(...existingTracks.map(t => new Date(t.releaseDate).getTime())))
            : null;
          
          console.log('🔍 [Track Preview] Latest existing track release date:', latestReleaseDate?.toDateString());
          
          // Find tracks that are truly new (not existing by title AND newer than latest release date)
          newTracks = allTracks.filter(itunesTrack => {
            // Skip tracks without release dates
            if (!itunesTrack.releaseDate) {
              return false;
            }
            
            const trackReleaseDate = new Date(itunesTrack.releaseDate);
            
            // Check if track already exists by title
            const trackExists = existingTracks.some(existing => 
              existing.title?.toLowerCase().trim() === itunesTrack.trackName?.toLowerCase().trim()
            );
            
            if (trackExists) {
              console.log(`🔍 [Track Preview] Track "${itunesTrack.trackName}" already exists - skipping`);
              return false;
            }
            
            // If no existing tracks, include all new tracks
            if (!latestReleaseDate) {
              console.log(`🔍 [Track Preview] Track "${itunesTrack.trackName}" (${trackReleaseDate.toDateString()}) - no existing tracks, including`);
              return true;
            }
            
            // Only include tracks newer than the latest existing track
            const isNewer = trackReleaseDate > latestReleaseDate;
            console.log(`🔍 [Track Preview] Track "${itunesTrack.trackName}" (${trackReleaseDate.toDateString()}) vs latest (${latestReleaseDate.toDateString()}) - ${isNewer ? 'NEWER ✅' : 'OLDER ❌'}`);
            
            return isNewer;
          }).map(track => ({
            trackId: track.trackId,
            trackName: track.trackName,
            artistName: track.artistName,
            releaseDate: track.releaseDate,
            artworkUrl100: track.artworkUrl100,
            previewUrl: track.previewUrl
          }));
          
          console.log('🔍 [Track Preview] Found new tracks:', newTracks.length);
        } catch (error) {
          console.warn('🔍 [Track Preview] Error fetching iTunes tracks:', error);
        }
      }
      
      // Update the cache
      setProfileTrackCounts(prev => ({
        ...prev,
        [profile._id]: {
          existing: existingTracks.length,
          new: newTracks.length
        }
      }));
      
      setTrackPreviewData({
        profile,
        existingTracks,
        newTracks,
        loading: false
      });
      
    } catch (error) {
      console.error('🔍 [Track Preview] Error:', error);
      message.error('Failed to preview tracks');
      setTrackPreviewData({
        profile,
        existingTracks: [],
        newTracks: [],
        loading: false,
        error: error.message
      });
    } finally {
      setTrackPreviewLoading(false);
    }
  };

  // Batch load track counts for multiple profiles
  const batchLoadTrackCounts = async (profileList, batchSize = 20) => {
    setIsBatchLoading(true);
    setBatchLoadingProgress(0);
    const newProfilesWithTracks = new Set();
    const trackCounts = {};
    
    try {
      console.log(`📊 [Batch Load] Starting to load track counts for ${profileList.length} profiles`);
      
      for (let i = 0; i < profileList.length; i += batchSize) {
        const batch = profileList.slice(i, Math.min(i + batchSize, profileList.length));
        setBatchLoadingProgress(Math.round((i / profileList.length) * 100));
        
        // Process batch in parallel
        const batchPromises = batch.map(async (profile) => {
          try {
            // Skip if already cached and not stale
            if (profileTrackCounts[profile._id] && !dateFilter) {
              const cached = profileTrackCounts[profile._id];
              if (cached.new > 0) {
                newProfilesWithTracks.add(profile._id);
              }
              return { profileId: profile._id, ...cached };
            }
            
            // Fetch existing tracks
            let existingTracks = [];
            let allExistingTitles = []; // Keep all titles for matching, even without dates
            try {
              const params = new URLSearchParams({
                publicationType: 'Track',
                pagination: 'true',
                optimized: 'true',
                limit: '100',
                page: '1'
              });
              const response = await apiClient.get(`/publications/profile/${profile._id}?${params.toString()}`);

              let publications = [];
              if (Array.isArray(response.data)) {
                publications = response.data;
              } else if (response.data?.data && Array.isArray(response.data.data)) {
                publications = response.data.data;
              }

              // Keep ALL titles for existence check (even without dates)
              allExistingTitles = publications.map(pub => {
                const publicationData = pub.publicationData || pub;
                return publicationData.title?.toLowerCase().trim();
              }).filter(Boolean);

              // Only use tracks WITH dates for date comparison
              existingTracks = publications.map(pub => {
                const publicationData = pub.publicationData || pub;
                return {
                  title: publicationData.title,
                  artist: publicationData.artist,
                  releaseDate: publicationData.releaseDate ? new Date(publicationData.releaseDate) : null
                };
              }).filter(track => track.title && track.releaseDate);

              console.log(`📊 [Batch Load] ${profile.username}: ${publications.length} total publications, ${existingTracks.length} with release dates, ${allExistingTitles.length} titles for matching`);
            } catch (error) {
              console.warn(`⚠️ [Batch Load] Could not fetch existing tracks for ${profile.username}:`, error.message);
            }

            // Get the latest release date from existing tracks
            const latestReleaseDate = existingTracks.length > 0
              ? new Date(Math.max(...existingTracks.map(t => t.releaseDate.getTime())))
              : null;

            console.log(`📊 [Batch Load] ${profile.username}: latestReleaseDate = ${latestReleaseDate?.toDateString() || 'null'}`);

            // Apply date filter if set
            let filterDate = null;
            const now = new Date();
            if (dateFilter === '2weeks') {
              filterDate = new Date(now.getTime() - (14 * 24 * 60 * 60 * 1000));
            } else if (dateFilter === '30days') {
              filterDate = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
            }

            // Use the more restrictive date (either latest existing or filter)
            const effectiveDate = filterDate && latestReleaseDate
              ? new Date(Math.max(filterDate.getTime(), latestReleaseDate.getTime()))
              : latestReleaseDate || filterDate;

            console.log(`📊 [Batch Load] ${profile.username}: effectiveDate = ${effectiveDate?.toDateString() || 'null (will include all new tracks)'}`);

            // Fetch iTunes tracks
            let newTrackCount = 0;
            if (profile.itunesMusicID) {
              try {
                const isDevelopment = window.location.hostname === 'localhost';
                const itunesUrl = `https://itunes.apple.com/lookup?id=${profile.itunesMusicID}&entity=song&limit=20`;

                let itunesResults = [];
                if (isDevelopment) {
                  const corsProxies = [
                    'https://api.allorigins.win/get?url=',
                    'https://corsproxy.io/?',
                  ];

                  for (const proxy of corsProxies) {
                    try {
                      let finalUrl, responseData;
                      if (proxy.includes('allorigins')) {
                        finalUrl = `${proxy}${encodeURIComponent(itunesUrl)}`;
                        const response = await axios.get(finalUrl, { timeout: 20000 });
                        responseData = JSON.parse(response.data.contents);
                      } else {
                        finalUrl = `${proxy}${itunesUrl}`;
                        const response = await axios.get(finalUrl, { timeout: 20000 });
                        responseData = response.data;
                      }

                      itunesResults = responseData.results || [];
                      console.log(`📊 [Batch Load] ${profile.username}: iTunes returned ${itunesResults.length} results via ${proxy}`);
                      break;
                    } catch (proxyError) {
                      console.warn(`📊 [Batch Load] ${profile.username}: Proxy ${proxy} failed:`, proxyError.message);
                      continue;
                    }
                  }
                } else {
                  const response = await axios.get(itunesUrl, { timeout: 20000 });
                  itunesResults = response.data.results || [];
                }

                const allTracks = itunesResults.filter(r => r.wrapperType === 'track');
                console.log(`📊 [Batch Load] ${profile.username}: ${allTracks.length} iTunes tracks to check`);

                // Count new tracks based on date filter
                const newTracks = allTracks.filter(itunesTrack => {
                  if (!itunesTrack.releaseDate) return false;

                  const trackReleaseDate = new Date(itunesTrack.releaseDate);
                  const normalizedItunesTitle = itunesTrack.trackName?.toLowerCase().trim();

                  // Check if track already exists by title (using ALL existing titles)
                  const trackExists = allExistingTitles.includes(normalizedItunesTitle);

                  if (trackExists) {
                    console.log(`📊 [Batch Load] ${profile.username}: "${itunesTrack.trackName}" - SKIP (already exists)`);
                    return false;
                  }

                  // If no effective date, include all non-existing tracks
                  if (!effectiveDate) {
                    console.log(`📊 [Batch Load] ${profile.username}: "${itunesTrack.trackName}" (${trackReleaseDate.toDateString()}) - NEW (no date filter)`);
                    return true;
                  }

                  // Check if track is newer than effective date
                  const isNewer = trackReleaseDate >= effectiveDate;
                  console.log(`📊 [Batch Load] ${profile.username}: "${itunesTrack.trackName}" (${trackReleaseDate.toDateString()}) vs ${effectiveDate.toDateString()} - ${isNewer ? 'NEW ✅' : 'OLD ❌'}`);
                  return isNewer;
                });

                newTrackCount = newTracks.length;
                console.log(`📊 [Batch Load] ${profile.username}: Found ${newTrackCount} new tracks to sync`);

              } catch (error) {
                console.error(`⚠️ [Batch Load] Could not fetch iTunes tracks for ${profile.username}:`, error.message);
              }
            } else {
              console.log(`📊 [Batch Load] ${profile.username}: No iTunes ID, skipping`);
            }
            
            if (newTrackCount > 0) {
              newProfilesWithTracks.add(profile._id);
            }
            
            return {
              profileId: profile._id,
              existing: existingTracks.length,
              new: newTrackCount
            };
            
          } catch (error) {
            console.error(`❌ [Batch Load] Error processing ${profile.username}:`, error);
            return {
              profileId: profile._id,
              existing: 0,
              new: 0
            };
          }
        });
        
        const batchResults = await Promise.all(batchPromises);
        
        // Update track counts
        batchResults.forEach(result => {
          trackCounts[result.profileId] = {
            existing: result.existing,
            new: result.new
          };
        });
        
        // Update state with batch results
        setProfileTrackCounts(prev => ({
          ...prev,
          ...trackCounts
        }));
        
        console.log(`📊 [Batch Load] Processed batch ${Math.ceil(i / batchSize) + 1}/${Math.ceil(profileList.length / batchSize)}`);
        
        // Small delay between batches to avoid overwhelming the API
        if (i + batchSize < profileList.length) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      
      setProfilesWithNewTracks(newProfilesWithTracks);
      setBatchLoadingProgress(100);
      
      const profilesWithNewCount = newProfilesWithTracks.size;
      console.log(`📊 [Batch Load] Complete! ${profilesWithNewCount} profiles have new tracks out of ${profileList.length} total`);
      
      return { trackCounts, profilesWithNewTracks: newProfilesWithTracks };
      
    } catch (error) {
      console.error('❌ [Batch Load] Error:', error);
      message.error('Failed to load track counts');
    } finally {
      setIsBatchLoading(false);
      setBatchLoadingProgress(0);
    }
  };

  // Open sync options modal
  const openSyncOptionsModal = async () => {
    setSyncOptionsModalVisible(true);
    setSelectedProfilesToSync([]);
    setSelectedCityProfiles([]);
    setSelectedSyncCity(null);
    setProfileSyncMode('all');
    setDateFilter('all'); // Reset date filter
    
    let profilesToLoad = profilesForSync;
    
    if (profilesForSync.length === 0) {
      profilesToLoad = await fetchProfilesForSync();
    }
    
    // DON'T auto-load - let user choose mode first, then load relevant profiles only
    // This prevents scanning all 659 profiles when user only wants to sync selected ones
    console.log(`📋 [Sync Modal] Found ${profilesToLoad?.length || 0} profiles available for sync. User will choose which to check.`);
  };
  
  // Handle selective sync (selected profiles only)
  const handleSelectiveSync = async () => {
    if (selectedProfilesToSync.length === 0) {
      message.warning('Please select profiles to sync');
      return;
    }
    
    setSyncOptionsModalVisible(false);
    setSyncAllProcessing(true);
    setSyncProgress(0);
    setSyncTotal(selectedProfilesToSync.length);
    setSyncCurrentProfile('');
    setCancelSyncOperation(false);
    
    try {
      console.log(`🔄 [SelectiveSync] Starting sync for ${selectedProfilesToSync.length} selected profiles`);
      
      let totalSuccessCount = 0;
      let totalErrorCount = 0;
      
      for (let i = 0; i < selectedProfilesToSync.length; i++) {
        const profileId = selectedProfilesToSync[i];
        const profile = profilesForSync.find(p => p._id === profileId);
        
        if (!profile) continue;
        
        if (cancelSyncOperation) {
          console.log('🔄 [SelectiveSync] Operation cancelled');
          break;
        }
        
        setSyncCurrentProfile(profile.username);
        setSyncProgress(i + 1);
        
        // Use existing sync logic for each profile
        const { successCount, errorCount } = await syncSingleProfile(profile);
        totalSuccessCount += successCount;
        totalErrorCount += errorCount;
        
        // Rate limiting between profiles
        if (i < selectedProfilesToSync.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }
      
      // Show results
      if (totalSuccessCount > 0) {
        message.success(`Successfully synced ${totalSuccessCount} tracks from ${selectedProfilesToSync.length} profiles!`);
      }
      if (totalErrorCount > 0) {
        message.warning(`Failed to sync ${totalErrorCount} tracks`);
      }
      
    } catch (error) {
      console.error('Error in selective sync:', error);
      message.error('Failed to complete selective sync');
    } finally {
      setSyncAllProcessing(false);
      setSyncProgress(0);
      setSyncTotal(0);
      setSyncCurrentProfile('');
      setCancelSyncOperation(false);
    }
  };
  
  // Handle city-based sync
  const handleCitySync = async (cityId) => {
    const cityProfiles = profilesForSync.filter(profile => {
      const { cityId: profileCityId } = getCityInfo(profile);
      return profileCityId === cityId;
    });
    
    if (cityProfiles.length === 0) {
      const cityName = availableCities.find(c => c._id === cityId)?.name || 'Unknown';
      message.warning(`No profiles found for ${cityName}. This may be due to profiles with invalid city data.`);
      return;
    }

    // Log profiles with problematic city data
    const profilesWithInvalidCity = profilesForSync.filter(profile => {
      const { cityId: profileCityId } = getCityInfo(profile);
      return !profileCityId;
    });
    
    if (profilesWithInvalidCity.length > 0) {
      console.warn(`Found ${profilesWithInvalidCity.length} profiles with invalid city data:`, 
        profilesWithInvalidCity.map(p => p.username));
    }
    
    setSyncOptionsModalVisible(false);
    setSyncAllProcessing(true);
    setSyncProgress(0);
    setSyncTotal(cityProfiles.length);
    setSyncCurrentProfile('');
    setCancelSyncOperation(false);
    
    try {
      const cityName = availableCities.find(c => c._id === cityId)?.name || 'Unknown';
      console.log(`🔄 [CitySync] Starting sync for ${cityProfiles.length} profiles from ${cityName}`);
      
      let totalSuccessCount = 0;
      let totalErrorCount = 0;
      
      for (let i = 0; i < cityProfiles.length; i++) {
        const profile = cityProfiles[i];
        
        if (cancelSyncOperation) {
          console.log('🔄 [CitySync] Operation cancelled');
          break;
        }
        
        setSyncCurrentProfile(profile.username);
        setSyncProgress(i + 1);
        
        // Use existing sync logic for each profile
        const { successCount, errorCount } = await syncSingleProfile(profile);
        totalSuccessCount += successCount;
        totalErrorCount += errorCount;
        
        // Rate limiting between profiles
        if (i < cityProfiles.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }
      
      // Show results
      if (totalSuccessCount > 0) {
        message.success(`Successfully synced ${totalSuccessCount} tracks from ${cityName}!`);
      }
      if (totalErrorCount > 0) {
        message.warning(`Failed to sync ${totalErrorCount} tracks`);
      }
      
    } catch (error) {
      console.error('Error in city sync:', error);
      message.error('Failed to complete city sync');
    } finally {
      setSyncAllProcessing(false);
      setSyncProgress(0);
      setSyncTotal(0);
      setSyncCurrentProfile('');
      setCancelSyncOperation(false);
    }
  };

  // Handle sync selected profiles
  const handleSyncSelectedProfiles = async (profileIds) => {
    if (!profileIds || profileIds.length === 0) {
      message.warning('No profiles selected to sync');
      return;
    }
    
    setSyncAllProcessing(true);
    setSyncProgress(0);
    setSyncTotal(profileIds.length);
    setSyncCurrentProfile('');
    setCancelSyncOperation(false);
    
    try {
      console.log(`🔄 [SelectedSync] Starting sync for ${profileIds.length} selected profiles`);
      console.log(`🔄 [SelectedSync] Profile IDs to sync:`, profileIds);
      console.log(`🔄 [SelectedSync] Available profiles in profilesForSync:`, profilesForSync.length);
      console.log(`🔄 [SelectedSync] Available profiles in main profiles:`, profiles?.length || 0);
      
      let totalSuccessCount = 0;
      let totalErrorCount = 0;
      
      for (let i = 0; i < profileIds.length; i++) {
        const profileId = profileIds[i];
        
        // Try to find profile in profilesForSync first, then fallback to all profiles
        let profile = profilesForSync.find(p => p._id === profileId);
        
        if (!profile && profiles && Array.isArray(profiles)) {
          profile = profiles.find(p => p._id === profileId);
        }
        
        if (!profile) {
          console.warn(`Profile with ID ${profileId} not found in any profile list`);
          totalErrorCount++;
          continue;
        }
        
        if (cancelSyncOperation) {
          console.log('🔄 [SelectedSync] Operation cancelled');
          break;
        }
        
        setSyncCurrentProfile(profile.username);
        setSyncProgress(i + 1);
        
        // Use existing sync logic for each profile
        const { successCount, errorCount } = await syncSingleProfile(profile);
        totalSuccessCount += successCount;
        totalErrorCount += errorCount;
        
        // Rate limiting between profiles
        if (i < profileIds.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }
      
      // Show results
      if (totalSuccessCount > 0) {
        message.success(`Successfully synced ${totalSuccessCount} tracks from ${profileIds.length} selected profiles!`);
      }
      if (totalErrorCount > 0) {
        message.warning(`Failed to sync ${totalErrorCount} tracks`);
      }
      if (totalSuccessCount === 0 && totalErrorCount === 0) {
        message.info('No new tracks were found to sync from the selected profiles');
      }
      
    } catch (error) {
      console.error('Error in selected profiles sync:', error);
      message.error('Failed to complete selected profiles sync');
    } finally {
      setSyncAllProcessing(false);
      setSyncProgress(0);
      setSyncTotal(0);
      setSyncCurrentProfile('');
      setCancelSyncOperation(false);
    }
  };
  
  // Extract single profile sync logic for reuse
  const syncSingleProfile = async (profile) => {
    let successCount = 0;
    let errorCount = 0;
    
    try {
      // Validate iTunes ID
      if (!profile.itunesMusicID || profile.itunesMusicID.trim() === '') {
        console.warn(`Profile ${profile.username} (${profile._id}) has no iTunes ID`);
        return { successCount: 0, errorCount: 0 };
      }
      
      console.log(`🔄 [Sync] Starting sync for ${profile.username} (iTunes ID: ${profile.itunesMusicID})`);
      setBatchCurrentTrack('Fetching existing tracks...');
      
      // Get existing tracks for this profile
      let existingTracks = [];
      let allExistingTitles = []; // Keep ALL titles for existence check
      try {
        const params = new URLSearchParams({
          publicationType: 'Track',
          pagination: 'true',
          optimized: 'true',
          limit: '100',
          page: '1'
        });
        const existingPublications = await apiClient.get(`/publications/profile/${profile._id}?${params.toString()}`);

        let publications = [];
        if (Array.isArray(existingPublications.data)) {
          publications = existingPublications.data;
        } else if (existingPublications.data?.data && Array.isArray(existingPublications.data.data)) {
          publications = existingPublications.data.data;
        }

        // Keep ALL titles for existence check (even without dates)
        allExistingTitles = publications.map(pub => {
          const publicationData = pub.publicationData || pub;
          return publicationData.title?.toLowerCase().trim();
        }).filter(Boolean);

        console.log(`🔄 [Sync] ${profile.username}: Found ${publications.length} existing publications, ${allExistingTitles.length} titles`);

        if (publications.length > 0) {
          // Only use tracks WITH dates for date comparison
          existingTracks = publications
            .map(pub => {
              const publicationData = pub.publicationData || pub;
              return {
                title: publicationData.title,
                artist: publicationData.artist,
                releaseDate: publicationData.releaseDate ? new Date(publicationData.releaseDate) : null
              };
            })
            .filter(track => track.title && track.releaseDate);
        }
      } catch (error) {
        console.warn(`Could not fetch existing tracks for ${profile.username}:`, error);
      }
      
      // Fetch iTunes tracks - use same limit as batchLoadTrackCounts (20) for consistency
      setBatchCurrentTrack('Searching iTunes catalog...');
      const itunesUrl = `https://itunes.apple.com/lookup?id=${profile.itunesMusicID}&entity=song&limit=20`;

      let itunesResults = [];
      const corsProxies = [
        'https://api.allorigins.win/get?url=',
        'https://corsproxy.io/?',
      ];

      for (const proxy of corsProxies) {
        try {
          let finalUrl, responseData;

          if (proxy.includes('allorigins')) {
            finalUrl = `${proxy}${encodeURIComponent(itunesUrl)}`;
            const response = await axios.get(finalUrl, { timeout: 20000 });
            responseData = JSON.parse(response.data.contents);
          } else {
            finalUrl = `${proxy}${itunesUrl}`;
            const response = await axios.get(finalUrl, { timeout: 20000 });
            responseData = response.data;
          }

          itunesResults = responseData.results || [];
          break;
        } catch (error) {
          continue;
        }
      }
      
      const allTracks = itunesResults.filter(r => r.wrapperType === 'track');
      console.log(`🔄 [Sync] ${profile.username}: Found ${allTracks.length} iTunes tracks`);

      // Filter for new tracks only - check BOTH title existence AND release date
      const latestReleaseDate = existingTracks.length > 0
        ? new Date(Math.max(...existingTracks.map(t => t.releaseDate.getTime())))
        : null;

      console.log(`🔄 [Sync] ${profile.username}: Latest existing release date: ${latestReleaseDate?.toDateString() || 'none'}`);

      let tracksToSync = allTracks.filter(track => {
        if (!track.releaseDate) return false;

        const trackReleaseDate = new Date(track.releaseDate);
        const normalizedTitle = track.trackName?.toLowerCase().trim();

        // First check: Does this track already exist by title?
        const trackExists = allExistingTitles.includes(normalizedTitle);
        if (trackExists) {
          console.log(`🔄 [Sync] ${profile.username}: "${track.trackName}" - SKIP (already exists)`);
          return false;
        }

        // Second check: Is it newer than our latest existing track? (use >= for same-day releases)
        if (latestReleaseDate && trackReleaseDate < latestReleaseDate) {
          console.log(`🔄 [Sync] ${profile.username}: "${track.trackName}" (${trackReleaseDate.toDateString()}) - SKIP (older than ${latestReleaseDate.toDateString()})`);
          return false;
        }

        console.log(`🔄 [Sync] ${profile.username}: "${track.trackName}" (${trackReleaseDate.toDateString()}) - WILL SYNC ✅`);
        return true;
      });
      
      // Safety limit
      if (tracksToSync.length > 10) {
        tracksToSync = tracksToSync
          .sort((a, b) => new Date(b.releaseDate) - new Date(a.releaseDate))
          .slice(0, 10);
      }
      
      // Sync each track
      setBatchTotal(tracksToSync.length);
      for (let i = 0; i < tracksToSync.length; i++) {
        if (cancelSyncOperation) break;
        
        const track = tracksToSync[i];
        setBatchCurrentTrack(`${track.trackName} (${i + 1}/${tracksToSync.length})`);
        setBatchProgress(i + 1);
        
        try {
          const payload = {
            title: track.trackName,
            artist: track.artistName,
            musicTypes: [],
            creatorProfileId: profile._id,
            flyer: track.artworkUrl100?.replace('100x100', '600x600') || null,
            buyLink: track.trackViewUrl || null,
            streamLink: track.previewUrl || null,
            label: track.recordLabel || track.artistName || null,
            releaseDate: track.releaseDate ? new Date(track.releaseDate).toISOString() : null,
            duration: track.trackTimeMillis ? Math.floor(track.trackTimeMillis / 1000) : null,
            trackType: track.wrapperType === 'track' ? 'Single' : 'Album',
            originCountry: track.country || null,
            isTrending: false,
            moodTags: [],
            affiliatedProfiles: [],
          };
          
          // Map iTunes genre to music types
          if (track.primaryGenreName) {
            const foundGenre = musicGenres.find(g => g.value.toLowerCase() === track.primaryGenreName.toLowerCase());
            if (foundGenre) {
              payload.musicTypes = [foundGenre._id];
            }
          }
          
          // Fallback to profile's music types
          if (payload.musicTypes.length === 0 && profile.musicTypes && profile.musicTypes.length > 0) {
            payload.musicTypes = profile.musicTypes.map(mt => typeof mt === 'string' ? mt : mt._id);
          }
          
          // Clean up null values
          Object.keys(payload).forEach(key => {
            if (payload[key] === null || payload[key] === undefined) {
              delete payload[key];
            }
          });
          
          console.log(`🔄 [Sync] ${profile.username}: Creating publication for "${track.trackName}"...`);
          console.log(`🔄 [Sync] Payload:`, payload);
          const createResponse = await apiClient.post('/tracks/backoffice/create', payload);
          console.log(`🔄 [Sync] ${profile.username}: ✅ Created "${track.trackName}" - Response:`, createResponse.data);
          successCount++;
        } catch (error) {
          errorCount++;
          console.error(`❌ [Sync] Failed to sync track: ${track.trackName}`, error);
          console.error(`❌ [Sync] Error response:`, error.response?.data);
        }
        
        // Rate limiting
        if (i < tracksToSync.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      
    } catch (error) {
      console.error(`Error syncing profile ${profile.username}:`, error);
    } finally {
      setBatchProgress(0);
      setBatchTotal(0);
      setBatchCurrentTrack('');
    }
    
    return { successCount, errorCount };
  };
  
  // Sync tracks for all profiles with iTunes IDs
  const handleSyncAllProfiles = async () => {
    setSyncAllProcessing(true);
    setSyncAllPreviewVisible(false);
    setSyncProgress(0);
    setSyncTotal(0);
    setSyncCurrentProfile('');
    setCancelSyncOperation(false);
    setBatchProgress(0);
    setBatchTotal(0);
    setBatchCurrentTrack('');

    try {
      console.log('🔄 [SyncAll] Starting sync all profiles...');
      
      // 1. Fetch profiles in pages of 100
      console.log('🔄 [SyncAll] Fetching profiles in pages of 100...');
      let allProfilesWithItunes = [];
      let currentPage = 1;
      const pageSize = 100;
      let hasMorePages = true;
      
      while (hasMorePages) {
        console.log(`🔄 [SyncAll] Fetching page ${currentPage}...`);
        const profilesResponse = await fetchProfiles(false, currentPage, pageSize, true, true, '', 'M');
        
        let pageProfiles = [];
        if (Array.isArray(profilesResponse.data)) {
          pageProfiles = profilesResponse.data;
        } else if (profilesResponse.data?.data && Array.isArray(profilesResponse.data.data)) {
          pageProfiles = profilesResponse.data.data;
        } else if (Array.isArray(profilesResponse)) {
          pageProfiles = profilesResponse;
        }
        
        console.log(`🔄 [SyncAll] Page ${currentPage}: Found ${pageProfiles.length} profiles`);
        
        // Filter profiles with iTunes IDs on this page
        const pageProfilesWithItunes = pageProfiles.filter(profile => {
          const hasItunesId = profile.itunesMusicID && profile.itunesMusicID.trim() !== '';
          const isMusician = profile.profileType === 'M';
          return hasItunesId && isMusician;
        });
        
        allProfilesWithItunes = [...allProfilesWithItunes, ...pageProfilesWithItunes];
        console.log(`🔄 [SyncAll] Page ${currentPage}: Found ${pageProfilesWithItunes.length} profiles with iTunes IDs`);
        
        // Check if we should continue to next page
        hasMorePages = pageProfiles.length === pageSize;
        currentPage++;
        
        // Safety limit to prevent infinite loops
        if (currentPage > 50) {
          console.warn('🔄 [SyncAll] Reached page limit (50), stopping');
          break;
        }
      }
      
      console.log(`🔄 [SyncAll] Total profiles with iTunes IDs found: ${allProfilesWithItunes.length}`);
      console.log(`🔄 [SyncAll] Profiles with iTunes IDs:`, allProfilesWithItunes.map(p => `${p.username} (${p.itunesMusicID})`));
      
      const profilesWithItunes = allProfilesWithItunes;

      if (profilesWithItunes.length === 0) {
        message.info('No profiles with iTunes IDs found');
        return;
      }

      setSyncTotal(profilesWithItunes.length);
      setTotalPages(Math.ceil(profilesWithItunes.length / 100));
      let totalSuccessCount = 0;
      let totalErrorCount = 0;
      let profilesWithNewTracks = 0;
      let profilesProcessed = 0;

      // 2. Process profiles in batches of 100
      const batchSize = 100;
      for (let batchStart = 0; batchStart < profilesWithItunes.length; batchStart += batchSize) {
        const batchEnd = Math.min(batchStart + batchSize, profilesWithItunes.length);
        const currentBatch = profilesWithItunes.slice(batchStart, batchEnd);
        const batchNumber = Math.floor(batchStart / batchSize) + 1;
        
        setCurrentPage(batchNumber);
        console.log(`🔄 [SyncAll] Processing batch ${batchNumber}/${Math.ceil(profilesWithItunes.length / batchSize)} (${currentBatch.length} profiles)`);
        
        // Process each profile in the current batch
        for (let i = 0; i < currentBatch.length; i++) {
          const profileIndex = batchStart + i;
          const profile = currentBatch[i];
        // Check for cancellation
        if (cancelSyncOperation) {
          console.log('🔄 [SyncAll] Operation cancelled by user');
          message.info('Sync all operation cancelled');
          break;
        }

          const profileDisplayName = profile.username || profile.name || 'Unknown Profile';
          setSyncCurrentProfile(profileDisplayName);
          setSyncProgress(profileIndex + 1);
          setBatchCurrentTrack('Analyzing profile...');
          setBatchProgress(0);
          setBatchTotal(0);

          console.log(`🔄 [SyncAll] [${profileIndex + 1}/${profilesWithItunes.length}] Processing: ${profileDisplayName} (iTunes ID: ${profile.itunesMusicID})`);

          try {
          // Step 1: Get existing publications for this profile
          setBatchCurrentTrack('Fetching existing tracks...');
          let existingTracks = [];
          let existingTracksCount = 0;
          
          try {
            const params = new URLSearchParams({
              publicationType: 'Track',
              pagination: 'true',
              optimized: 'true',
              limit: '100',
              page: '1'
            });
            const existingPublications = await apiClient.get(`/publications/profile/${profile._id}?${params.toString()}`);
            
            let publications = [];
            if (Array.isArray(existingPublications.data)) {
              publications = existingPublications.data;
            } else if (existingPublications.data?.data && Array.isArray(existingPublications.data.data)) {
              publications = existingPublications.data.data;
            }
            
            existingTracksCount = publications.length;
            
            if (publications.length > 0) {
              existingTracks = publications
                .map(pub => {
                  const publicationData = pub.publicationData || pub;
                  return {
                    title: publicationData.title,
                    artist: publicationData.artist,
                    releaseDate: publicationData.releaseDate ? new Date(publicationData.releaseDate) : null
                  };
                })
                .filter(track => track.title && track.releaseDate);
              
              // Find the latest release date from existing tracks
              const latestExistingDate = existingTracks.length > 0 
                ? new Date(Math.max(...existingTracks.map(t => t.releaseDate.getTime())))
                : null;
              
              console.log(`🔄 [SyncAll] ${profileDisplayName}: Found ${existingTracksCount} existing tracks with ${existingTracks.length} having release dates`);
              console.log(`🔄 [SyncAll] ${profileDisplayName}: Latest existing track release date: ${latestExistingDate?.toDateString()}`);
            }
            
            console.log(`🔄 [SyncAll] ${profileDisplayName}: Found ${existingTracksCount} existing tracks`);
          } catch (error) {
            console.warn(`⚠️ [SyncAll] Could not fetch publications for ${profileDisplayName}:`, error);
          }

          // Step 2: Fetch iTunes tracks for this profile
          setBatchCurrentTrack('Searching iTunes catalog...');
          const isDevelopment = window.location.hostname === 'localhost';
          const itunesUrl = `https://itunes.apple.com/lookup?id=${profile.itunesMusicID}&entity=song&limit=10`;
          
          let itunesResults = [];
          let itunesError = null;
          
          if (isDevelopment) {
            const corsProxies = [
              'https://api.allorigins.win/get?url=',
              'https://corsproxy.io/?',
            ];

            for (const proxy of corsProxies) {
              try {
                console.log(`🔄 [SyncAll] ${profileDisplayName}: Trying iTunes fetch with proxy: ${proxy}`);
                let finalUrl, responseData;

                if (proxy.includes('allorigins')) {
                  finalUrl = `${proxy}${encodeURIComponent(itunesUrl)}`;
                  const response = await axios.get(finalUrl, { timeout: 20000 });
                  responseData = JSON.parse(response.data.contents);
                } else {
                  finalUrl = `${proxy}${itunesUrl}`;
                  const response = await axios.get(finalUrl, { timeout: 20000 });
                  responseData = response.data;
                }

                itunesResults = responseData.results || [];
                console.log(`🔄 [SyncAll] ${profileDisplayName}: Successfully fetched iTunes data with ${itunesResults.length} results`);
                break;
              } catch (error) {
                console.log(`🔄 [SyncAll] ${profileDisplayName}: Failed with proxy ${proxy}:`, error.message);
                itunesError = error;
                continue;
              }
            }
          } else {
            try {
              const response = await axios.get(itunesUrl);
              itunesResults = response.data.results || [];
            } catch (error) {
              itunesError = error;
            }
          }

          if (itunesResults.length === 0) {
            console.log(`⚠️ [SyncAll] ${profileDisplayName}: No iTunes data found ${itunesError ? '(Error: ' + itunesError.message + ')' : ''}`);
            profilesProcessed++;
            continue;
          }

          const allTracks = itunesResults.filter(r => r.wrapperType === 'track');
          console.log(`🔄 [SyncAll] ${profileDisplayName}: Found ${allTracks.length} total iTunes tracks`);
          
          // Step 3: Filter for tracks newer than the latest existing track
          setBatchCurrentTrack('Comparing with existing tracks...');
          
          // Get the latest release date from existing tracks
          const latestReleaseDate = existingTracks.length > 0 
            ? new Date(Math.max(...existingTracks.map(t => t.releaseDate.getTime())))
            : null;
          
          let tracksToSync = allTracks.filter(track => {
            if (!track.releaseDate) return false;
            
            const trackReleaseDate = new Date(track.releaseDate);
            
            // If no existing tracks, sync all tracks
            if (!latestReleaseDate) {
              return true;
            }
            
            // Show only tracks newer than the latest existing track
            return trackReleaseDate > latestReleaseDate;
          });

          // SAFETY LIMIT: Never sync more than 50 tracks per profile to protect system
          if (tracksToSync.length > 10) {
            console.log(`⚠️ [SyncAll] ${profileDisplayName}: Limiting from ${tracksToSync.length} to 10 tracks for system safety`);
            tracksToSync = tracksToSync
              .sort((a, b) => new Date(b.releaseDate) - new Date(a.releaseDate)) // Sort by newest first
              .slice(0, 10); // Take only the 10 most recent tracks
          }
          
          if (latestReleaseDate) {
            console.log(`🔄 [SyncAll] ${profileDisplayName}: Found ${tracksToSync.length} tracks newer than ${latestReleaseDate.toDateString()} (compared to ${existingTracks.length} existing tracks)`);
          } else {
            console.log(`🔄 [SyncAll] ${profileDisplayName}: No existing tracks, will sync all ${tracksToSync.length} tracks`);
          }

          if (tracksToSync.length === 0) {
            console.log(`✅ [SyncAll] ${profileDisplayName}: Already up to date - no new tracks to sync`);
            profilesProcessed++;
            continue;
          }

          // Step 4: Sync each new track
          profilesWithNewTracks++;
          setBatchTotal(tracksToSync.length);
          console.log(`🔄 [SyncAll] ${profileDisplayName}: Starting to sync ${tracksToSync.length} new tracks...`);
          
          let profileSuccessCount = 0;
          let profileErrorCount = 0;
          
          for (let trackIndex = 0; trackIndex < tracksToSync.length; trackIndex++) {
            // Check for cancellation before each track
            if (cancelSyncOperation) {
              console.log('🔄 [SyncAll] Operation cancelled during track processing');
              break;
            }

            const track = tracksToSync[trackIndex];
            setBatchCurrentTrack(`${track.trackName} (${trackIndex + 1}/${tracksToSync.length})`);
            setBatchProgress(trackIndex + 1);
            
            try {
              console.log(`🔄 [SyncAll] ${profileDisplayName}: Syncing track ${trackIndex + 1}/${tracksToSync.length}: "${track.trackName}"`);
              
              const payload = {
                title: track.trackName,
                artist: track.artistName,
                musicTypes: [],
                creatorProfileId: profile._id,
                flyer: track.artworkUrl100?.replace('100x100', '600x600') || null,
                buyLink: track.trackViewUrl || null,
                streamLink: track.previewUrl || null,
                label: track.recordLabel || track.artistName || null,
                releaseDate: track.releaseDate ? new Date(track.releaseDate).toISOString() : null,
                duration: track.trackTimeMillis ? Math.floor(track.trackTimeMillis / 1000) : null,
                trackType: track.wrapperType === 'track' ? 'Single' : 'Album',
                originCountry: track.country || null,
                isTrending: false,
                moodTags: [],
                affiliatedProfiles: [],
              };

              // Map iTunes genre to music types
              if (track.primaryGenreName) {
                const foundGenre = musicGenres.find(g => g.value.toLowerCase() === track.primaryGenreName.toLowerCase());
                if (foundGenre) {
                  payload.musicTypes = [foundGenre._id];
                }
              }

              // Fallback to profile's music types
              if (payload.musicTypes.length === 0 && profile.musicTypes && profile.musicTypes.length > 0) {
                payload.musicTypes = profile.musicTypes.map(mt => typeof mt === 'string' ? mt : mt._id);
              }

              // Clean up null values
              Object.keys(payload).forEach(key => {
                if (payload[key] === null || payload[key] === undefined) {
                  delete payload[key];
                }
              });

              const response = await apiClient.post('/tracks/backoffice/create', payload);
              
              if (response && response.data) {
                totalSuccessCount++;
                profileSuccessCount++;
                console.log(`✅ [SyncAll] ${profileDisplayName}: Successfully synced "${track.trackName}"`);
              }
            } catch (error) {
              totalErrorCount++;
              profileErrorCount++;
              console.error(`❌ [SyncAll] ${profileDisplayName}: Failed to sync "${track.trackName}":`, error);
            }

            // Rate limiting between tracks
            if (trackIndex < tracksToSync.length - 1) {
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          }

          console.log(`✅ [SyncAll] ${profileDisplayName}: Completed - ${profileSuccessCount} tracks synced, ${profileErrorCount} failed`);
          profilesProcessed++;

          } catch (error) {
            console.error(`❌ [SyncAll] Error processing profile ${profileDisplayName}:`, error);
            profilesProcessed++;
          }

          // Rate limiting between profiles within batch
          if (i < currentBatch.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        }
        
        // Rate limiting between batches
        if (batchStart + batchSize < profilesWithItunes.length) {
          console.log(`🔄 [SyncAll] Waiting before next batch...`);
          await new Promise(resolve => setTimeout(resolve, 5000));
        }
      }

    } catch (error) {
      console.error('🔄 [SyncAll] Fatal error during sync all operation:', error);
      message.error('Failed to sync all profiles: ' + error.message);
    } finally {
      setSyncAllProcessing(false);
      setSyncProgress(0);
      setSyncTotal(0);
      setSyncCurrentProfile('');
      setBatchProgress(0);
      setBatchTotal(0);
      setBatchCurrentTrack('');
      setCancelSyncOperation(false);
    }
  };

  return (
    <div>
      <Card 
        title="Artist Track Catalog" 
        className="mb-4"
        extra={
          <Button
            type="primary"
            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
            onClick={openSyncOptionsModal}
            loading={syncAllProcessing || previewLoading || loadingProfilesForSync}
            disabled={syncProcessing || batchProcessing || previewLoading}
          >
            SYNC PROFILES
          </Button>
        }
      >
        <Form layout="vertical">
          <Form.Item label="Select Artist Profile" required>
            <Select
              placeholder="Search for an artist profile"
              loading={searchLoading || profilesLoading}
              showSearch
              onSearch={handleProfileSearch}
              onChange={handleArtistProfileSelect}
              filterOption={false} // Disable client-side filtering since we use server-side search
              options={searchResults
                .filter(profile => profile.profileType !== 'ML')
                .map(profile => ({
                  value: profile._id,
                  label: (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar src={profile.picture} size={24} style={{ marginRight: 8 }} />
                      <span>{profile.username}</span>
                    </div>
                  ),
                }))}
            />
          </Form.Item>
          
          {/* iTunes ID input and SCAN MUSIC button */}
          <Form.Item label="iTunes ID" style={{ marginBottom: 0 }}>
            <Input
              style={{ width: 200, marginRight: 12 }}
              value={selectedProfile?.itunesId || ''}
              placeholder="Enter iTunes ID"
              onChange={e => {
                if (selectedProfile) {
                  setSelectedProfile({ ...selectedProfile, itunesId: e.target.value });
                }
              }}
              disabled={!selectedProfile}
            />
            <Button
              type="primary"
              onClick={async () => {
                if (!selectedProfile?.itunesId) {
                  message.warning('Please enter an iTunes Artist ID');
                  return;
                }
                
                try {
                  // Check if we need to update the profile's iTunes ID
                  const currentItunesId = selectedProfile.itunesId;
                  const profileItunesId = selectedProfile.itunesMusicID;
                  
                  if (currentItunesId !== profileItunesId) {
                    console.log('🎵 [ArtistCatalog] Updating profile iTunes ID:', {
                      profileId: selectedProfile._id,
                      oldItunesId: profileItunesId,
                      newItunesId: currentItunesId
                    });
                    
                    // Update the profile with the new iTunes ID
                    await UpdateProfile(selectedProfile._id, {
                      itunesMusicID: currentItunesId
                    });
                    
                    // Update the local state to reflect the change
                    setSelectedProfile({ 
                      ...selectedProfile, 
                      itunesMusicID: currentItunesId 
                    });
                    
                    message.success('Profile iTunes ID updated successfully!');
                  }
                  
                  // Fetch iTunes tracks
                  fetchItunesArtistTracks(selectedProfile.itunesId);
                  fetchItunesTopTracks(selectedProfile.itunesId);
                } catch (error) {
                  console.error('🎵 [ArtistCatalog] Error updating profile iTunes ID:', error);
                  message.error('Failed to update profile iTunes ID');
                }
              }}
              disabled={!selectedProfile}
            >
              SCAN MUSIC
            </Button>
            <Button
              style={{ marginLeft: 8, backgroundColor: '#faad14', borderColor: '#faad14' }}
              type="primary"
              onClick={previewSyncTracks}
              disabled={!selectedProfile || !selectedProfile.itunesMusicID || syncProcessing || syncAllProcessing || batchProcessing || previewLoading}
              loading={previewLoading}
            >
              SYNC
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* Three-column layout for publications, iTunes tracks, and top tracks */}
      <div style={{ display: 'flex', flexDirection: 'row', gap: 16, alignItems: 'flex-start', width: '100%' }}>
        {/* Your Publications */}
        <div style={{ flex: 1, minWidth: 280 }}>
          <Card title="Your Publications" size="small" className="mb-4">
            {artistTracksLoading ? (
              <div className="text-center">
                <Spin size="large" />
                <p className="mt-2">Loading artist tracks...</p>
              </div>
            ) : artistTracks.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {artistTracks
                  .slice()
                  .sort((a, b) => {
                    const ta = (a.publicationData || a).releaseDate;
                    const tb = (b.publicationData || b).releaseDate;
                    return (tb ? new Date(tb).getTime() : 0) - (ta ? new Date(ta).getTime() : 0);
                  })
                  .map((track) => {
                    const t = track.publicationData || track;
                    return (
                      <Card
                        key={track._id}
                        size="small"
                        hoverable
                        style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: 4, padding: 0 }}
                        bodyStyle={{ display: 'flex', flexDirection: 'row', alignItems: 'center', padding: 8 }}
                      >
                        <div style={{ width: 48, height: 48, overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa', borderRadius: 4 }}>
                          <Image
                            alt={t.title}
                            src={t.flyer || t.cover}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            preview={false}
                          />
                        </div>
                        <div style={{ padding: 8, flex: 1 }}>
                          <div style={{ fontWeight: 500, fontSize: 14 }}>{t.title}</div>
                          <div style={{ color: '#888', fontSize: 12 }}>{t.artist}</div>
                          <div style={{ color: '#888', fontSize: 12 }}>{t.releaseDate ? moment(t.releaseDate).format('YYYY-MM-DD') : ''}</div>
                        </div>
                        <Button
                          danger
                          size="small"
                          style={{ marginLeft: 8 }}
                          onClick={async () => {
                            try {
                              await apiClient.delete(`/tracks/delete/${track._id}`);
                              message.success('Track deleted');
                              setArtistTracks(prev => prev.filter(tk => tk._id !== track._id));
                            } catch (err) {
                              message.error('Failed to delete track');
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </Card>
                    );
                  })}
              </div>
            ) : selectedProfile ? (
              <div className="text-center mt-4">
                <p>No tracks found for this artist.</p>
              </div>
            ) : null}
          </Card>
        </div>
        
        {/* iTunes Releases */}
        <div style={{ flex: 1, minWidth: 280 }}>
          <Card title="iTunes Releases" size="small" className="mb-4">
            {itunesTracksLoading ? (
              <div className="text-center">
                <Spin size="large" />
                <p className="mt-2">Fetching iTunes music...</p>
              </div>
            ) : itunesTracks.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {itunesTracks.map(track => (
                  <Card
                    key={track.trackId}
                    size="small"
                    style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: 4, padding: 0 }}
                    bodyStyle={{ display: 'flex', flexDirection: 'row', alignItems: 'center', padding: 8 }}
                  >
                    <div style={{ width: 48, height: 48, overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa', borderRadius: 4 }}>
                      <Image
                        alt={track.trackName}
                        src={track.artworkUrl100?.replace('100x100', '600x600')}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        preview={false}
                      />
                    </div>
                    <div style={{ padding: 8, flex: 1 }}>
                      <div style={{ fontWeight: 500, fontSize: 14 }}>{track.trackName}</div>
                      <div style={{ color: '#888', fontSize: 12 }}>{track.artistName}</div>
                      <div style={{ color: '#888', fontSize: 12 }}>{track.releaseDate ? moment(track.releaseDate).format('YYYY-MM-DD') : ''}</div>
                    </div>
                    <Button
                      type="primary"
                      size="small"
                      style={{ marginLeft: 8 }}
                      onClick={() => prepareTrackFromItunes(track)}
                    >
                      Add as Track Publication
                    </Button>
                  </Card>
                ))}
              </div>
            ) : null}
          </Card>
        </div>
        
        {/* Top Tracks */}
        <div style={{ flex: 1, minWidth: 320 }}>
          <Card 
            title="Top Tracks"
            size="small" 
            className="mb-4"
            extra={
              itunesTopTracks.length > 0 && !batchProcessing ? (
                <Button 
                  type="primary" 
                  size="small"
                  onClick={handleBatchAddTracks}
                  disabled={!selectedProfile}
                >
                  Add Batch ({itunesTopTracks.length})
                </Button>
              ) : null
            }
          >
            {batchProcessing ? (
              <div className="text-center" style={{ padding: '20px 0' }}>
                <Spin size="large" />
                <p className="mt-2">Adding tracks in batch...</p>
                <p className="text-sm text-gray-600">
                  Processing: {batchCurrentTrack}
                </p>
                <Progress 
                  percent={Math.round((batchProgress / batchTotal) * 100)}
                  status="active"
                  format={(percent) => `${batchProgress}/${batchTotal} (${percent}%)`}
                  style={{ marginTop: 16 }}
                />
              </div>
            ) : itunesTopTracksLoading ? (
              <div className="text-center">
                <Spin size="large" />
                <p className="mt-2">Fetching top tracks...</p>
              </div>
            ) : itunesTopTracks.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {itunesTopTracks.map(track => (
                  <Card
                    key={track.trackId}
                    size="small"
                    style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: 4, padding: 0 }}
                    bodyStyle={{ display: 'flex', flexDirection: 'row', alignItems: 'center', padding: 8 }}
                  >
                    <div style={{ width: 48, height: 48, overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa', borderRadius: 4 }}>
                      <Image
                        alt={track.trackName}
                        src={track.artworkUrl100?.replace('100x100', '600x600')}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        preview={false}
                      />
                    </div>
                    <div style={{ padding: 8, flex: 1 }}>
                      <div style={{ fontWeight: 500, fontSize: 14 }}>{track.trackName}</div>
                      <div style={{ color: '#888', fontSize: 12 }}>{track.artistName}</div>
                      <div style={{ color: '#888', fontSize: 12 }}>{track.releaseDate ? moment(track.releaseDate).format('YYYY-MM-DD') : ''}</div>
                    </div>
                    <Button
                      type="primary"
                      size="small"
                      style={{ marginLeft: 8 }}
                      onClick={() => prepareTrackFromItunes(track)}
                    >
                      Add as Track Publication
                    </Button>
                  </Card>
                ))}
              </div>
            ) : null}
          </Card>
        </div>
      </div>

      {/* Preview Loading Modal */}
      <Modal
        title="Loading Sync Preview"
        open={previewLoading}
        footer={null}
        closable={false}
        width={400}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Spin size="large" />
          <p style={{ marginTop: 16, fontSize: '16px' }}>
            Analyzing tracks to sync...
          </p>
          <p style={{ color: '#666', fontSize: '14px' }}>
            This may take a moment while we check for new releases.
          </p>
        </div>
      </Modal>

      {/* Single Profile Sync Preview Modal */}
      <Modal
        title={`Sync Preview - ${selectedProfile?.username}`}
        open={syncPreviewVisible}
        onOk={handleSyncProfile}
        onCancel={() => setSyncPreviewVisible(false)}
        okText={`Sync ${tracksToSync.length} Tracks`}
        cancelText="Cancel"
        confirmLoading={syncProcessing}
        width={700}
      >
        <div>
          <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f9f9f9', borderRadius: 4 }}>
            <p style={{ margin: 0, marginBottom: 8 }}>
              <strong>Profile:</strong> {selectedProfile?.username} &nbsp;
              <strong>iTunes ID:</strong> {selectedProfile?.itunesMusicID}
            </p>
            <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
              <strong>Existing Tracks:</strong> {existingTracksInfo.count} published tracks
              {existingTracksInfo.latestDate && (
                <span> (Latest: {moment(existingTracksInfo.latestDate).format('MMM DD, YYYY')})</span>
              )}
            </p>
          </div>
          
          {tracksToSync.length > 0 ? (
            <>
              <p style={{ color: '#52c41a', fontWeight: 500, marginBottom: 16 }}>
                ✅ Found {tracksToSync.length} new tracks to sync:
              </p>
              <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 4, padding: 8 }}>
                {tracksToSync.map((track, index) => (
                  <div key={track.trackId} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    padding: '8px 0', 
                    borderBottom: index < tracksToSync.length - 1 ? '1px solid #f5f5f5' : 'none' 
                  }}>
                    <Image
                      src={track.artworkUrl100}
                      alt={track.trackName}
                      width={40}
                      height={40}
                      style={{ borderRadius: 4, marginRight: 12 }}
                      preview={false}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500 }}>{track.trackName}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {track.artistName} • {moment(track.releaseDate).format('MMM DD, YYYY')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <p style={{ fontSize: '12px', color: '#666', marginTop: 12 }}>
                Only tracks newer than existing publications will be synced.
                {existingTracksInfo.latestDate && (
                  <span> Showing tracks released after {moment(existingTracksInfo.latestDate).format('MMM DD, YYYY')}.</span>
                )}
              </p>
              
              {existingTracksInfo.tracks.length > 0 && (
                <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f9f9f9', borderRadius: 4 }}>
                  <p style={{ margin: 0, marginBottom: 8, fontWeight: 500, fontSize: '14px' }}>
                    Latest Existing Tracks:
                  </p>
                  {existingTracksInfo.tracks.map((track, index) => (
                    <div key={index} style={{ fontSize: '12px', color: '#666', marginBottom: 4 }}>
                      • {track.title} ({moment(track.releaseDate).format('MMM DD, YYYY')})
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <p style={{ fontSize: '16px', color: '#999' }}>
                🎵 No new tracks found to sync
              </p>
              <p style={{ fontSize: '14px', color: '#666' }}>
                This profile is already up to date with their latest iTunes releases.
              </p>
              {existingTracksInfo.count > 0 && (
                <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f9f9f9', borderRadius: 4 }}>
                  <p style={{ margin: 0, marginBottom: 8, fontWeight: 500, fontSize: '14px' }}>
                    Current Status:
                  </p>
                  <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                    • {existingTracksInfo.count} tracks already published
                    {existingTracksInfo.latestDate && (
                      <span> • Latest track: {moment(existingTracksInfo.latestDate).format('MMM DD, YYYY')}</span>
                    )}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* All Profiles Sync Preview Modal */}
      <Modal
        title="Sync All Profiles Preview"
        open={syncAllPreviewVisible}
        onOk={handleSyncAllProfiles}
        onCancel={() => setSyncAllPreviewVisible(false)}
        okText={`Sync All (${allProfilesData.length} profiles)`}
        cancelText="Cancel"
        confirmLoading={syncAllProcessing}
        width={800}
      >
        <div>
          <p style={{ marginBottom: 16, fontSize: '16px' }}>
            <strong>Found {allProfilesData.length} profiles with iTunes IDs</strong>
          </p>
          
          <div style={{ maxHeight: 400, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 4, padding: 8 }}>
            {allProfilesData.map((item, index) => (
              <div key={item.profile._id} style={{ 
                display: 'flex', 
                alignItems: 'center', 
                padding: '8px 0', 
                borderBottom: index < allProfilesData.length - 1 ? '1px solid #f5f5f5' : 'none' 
              }}>
                <Avatar 
                  src={item.profile.picture} 
                  size={32} 
                  style={{ marginRight: 12 }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500 }}>{item.profile.username}</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    iTunes ID: {item.profile.itunesMusicID} • 
                    Existing: {item.existingCount} tracks • 
                    New: ~{item.estimatedNewTracks} tracks
                  </div>
                  {item.newTracksPreview && item.newTracksPreview.length > 0 && (
                    <div style={{ fontSize: '10px', color: '#999', marginTop: 4, fontStyle: 'italic' }}>
                      New tracks: {item.newTracksPreview.map(track => track.title).join(', ')}
                      {item.newTracksPreview.length >= 5 && '...'}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: '12px', color: '#999' }}>
                  iTunes ID: {item.profile.itunesMusicID}
                </div>
              </div>
            ))}
          </div>
          
          <div style={{ marginTop: 16, padding: 12, backgroundColor: '#fff7e6', border: '1px solid #ffd591', borderRadius: 4 }}>
            <p style={{ margin: 0, fontSize: '14px', color: '#d46b08' }}>
              <strong>⚠️ Warning:</strong> This operation may take 15-45 minutes depending on the number of profiles and tracks.
              You can cancel the operation at any time during processing.
            </p>
            <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#d46b08' }}>
              <strong>Safety Limit:</strong> Maximum 50 tracks per profile will be synced (most recent tracks first).
            </p>
          </div>
        </div>
      </Modal>

      {/* Sync Options Modal */}
      <Modal
        title="Sync Profile Options"
        open={syncOptionsModalVisible}
        onCancel={() => {
          setSyncOptionsModalVisible(false);
          setSelectedProfilesToSync([]);
          setSelectedSyncCity(null);
        }}
        width={900}
        footer={null}
      >
        <div>
          {/* Sync Mode Tabs */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <Button 
                type={profileSyncMode === 'all' ? 'primary' : 'default'}
                onClick={() => setProfileSyncMode('all')}
              >
                Sync All Profiles
              </Button>
              <Button 
                type={profileSyncMode === 'selected' ? 'primary' : 'default'}
                onClick={() => setProfileSyncMode('selected')}
              >
                Select Profiles
              </Button>
              <Button 
                type={profileSyncMode === 'byCity' ? 'primary' : 'default'}
                onClick={() => setProfileSyncMode('byCity')}
              >
                Sync by City
              </Button>
            </div>
            
            {/* Date Filter Buttons */}
            <div style={{ 
              display: 'flex', 
              gap: 8, 
              alignItems: 'center',
              padding: 12,
              backgroundColor: '#f5f5f5',
              borderRadius: 4
            }}>
              <Text strong>Filter tracks by release date:</Text>
              <Button
                size="small"
                type={dateFilter === 'all' ? 'primary' : 'default'}
                onClick={async () => {
                  setDateFilter('all');
                  // Only auto-load in "all" mode, not when user is selecting specific profiles
                  if (profileSyncMode === 'all' && profilesForSync.length > 0) {
                    batchLoadTrackCounts(profilesForSync, 20);
                  }
                }}
              >
                All New
              </Button>
              <Button
                size="small"
                type={dateFilter === '2weeks' ? 'primary' : 'default'}
                onClick={async () => {
                  setDateFilter('2weeks');
                  // Only auto-load in "all" mode
                  if (profileSyncMode === 'all' && profilesForSync.length > 0) {
                    batchLoadTrackCounts(profilesForSync, 20);
                  }
                }}
              >
                Past 2 Weeks
              </Button>
              <Button
                size="small"
                type={dateFilter === '30days' ? 'primary' : 'default'}
                onClick={async () => {
                  setDateFilter('30days');
                  // Only auto-load in "all" mode
                  if (profileSyncMode === 'all' && profilesForSync.length > 0) {
                    batchLoadTrackCounts(profilesForSync, 20);
                  }
                }}
              >
                Past 30 Days
              </Button>
              {isBatchLoading && (
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Spin size="small" />
                  <Text type="secondary">Loading track counts... {batchLoadingProgress}%</Text>
                </div>
              )}
            </div>
          </div>

          {/* Loading state */}
          {loadingProfilesForSync ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spin size="large" />
              <p style={{ marginTop: 16 }}>Loading profiles with iTunes IDs...</p>
            </div>
          ) : (
            <>
              {/* Sync All Mode */}
              {profileSyncMode === 'all' && (
                <div>
                  <Alert
                    message={
                      profilesWithNewTracks.size > 0 
                        ? `${profilesWithNewTracks.size} profiles have new tracks to sync (out of ${profilesForSync.length} total)`
                        : `Found ${profilesForSync.length} profiles with iTunes IDs`
                    }
                    description={
                      isBatchLoading 
                        ? `Loading track information... ${batchLoadingProgress}%`
                        : profilesWithNewTracks.size > 0
                        ? `Only profiles with new tracks will be synced. ${dateFilter === '2weeks' ? 'Showing tracks from past 2 weeks.' : dateFilter === '30days' ? 'Showing tracks from past 30 days.' : ''}`
                        : `Checking for new tracks across all profiles...`
                    }
                    type={profilesWithNewTracks.size > 0 ? "success" : "info"}
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                  
                  <div style={{ padding: '20px 0', borderTop: '1px solid #f0f0f0' }}>
                    <p style={{ marginBottom: 16 }}>
                      <strong>Cities represented:</strong> {availableCities.map(c => c.name || c._id).slice(0, 5).join(', ')}{availableCities.length > 5 ? ` and ${availableCities.length - 5} more...` : ''}
                    </p>
                    <p style={{ marginBottom: 16 }}>
                      <strong>Total profiles checked:</strong> {profilesForSync.length}
                    </p>
                    <p style={{ marginBottom: 16, fontSize: 16, color: '#52c41a' }}>
                      <strong>Profiles with new tracks:</strong> {profilesWithNewTracks.size}
                    </p>
                    {(() => {
                      const profilesWithoutCity = profilesForSync.filter(profile => {
                        const { cityId } = getCityInfo(profile);
                        return !cityId;
                      });
                      
                      if (profilesWithoutCity.length > 0) {
                        return (
                          <Alert
                            message="City Data Warning"
                            description={`${profilesWithoutCity.length} profiles have missing or invalid city data and will be excluded from city-based sync.`}
                            type="warning"
                            showIcon
                            style={{ marginBottom: 16 }}
                          />
                        );
                      }
                      return null;
                    })()}
                    <p style={{ color: '#faad14' }}>
                      <strong>⚠️ Note:</strong> Maximum 50 tracks per profile will be synced (most recent first)
                    </p>
                  </div>
                  
                  <div style={{ textAlign: 'right', marginTop: 24 }}>
                    <Button onClick={() => setSyncOptionsModalVisible(false)} style={{ marginRight: 8 }}>
                      Cancel
                    </Button>
                    <Button 
                      type="primary" 
                      onClick={() => {
                        setSyncOptionsModalVisible(false);
                        handleSyncAllProfiles();
                      }}
                      style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                    >
                      Start Sync All ({profilesForSync.length} profiles)
                    </Button>
                  </div>
                </div>
              )}

              {/* Select Profiles Mode */}
              {profileSyncMode === 'selected' && (
                <div>
                  <div style={{ marginBottom: 16 }}>
                    <Input.Search
                      placeholder="Search profiles by name..."
                      style={{ width: 300, marginRight: 16 }}
                      onSearch={(value) => {
                        // Filter profiles based on search
                        if (!value) {
                          setProfilesForSync(profilesForSync);
                        } else {
                          const filtered = profilesForSync.filter(p => 
                            p.username.toLowerCase().includes(value.toLowerCase())
                          );
                          setProfilesForSync(filtered);
                        }
                      }}
                      allowClear
                    />
                    <Button
                      onClick={() => setSelectedProfilesToSync(profilesForSync.map(p => p._id))}
                      style={{ marginRight: 8 }}
                    >
                      Select All
                    </Button>
                    <Button
                      onClick={() => setSelectedProfilesToSync([])}
                      style={{ marginRight: 8 }}
                    >
                      Clear Selection
                    </Button>
                    <Button
                      type="primary"
                      onClick={() => {
                        if (selectedProfilesToSync.length === 0) {
                          message.warning('Select profiles first');
                          return;
                        }
                        const selectedProfiles = profilesForSync.filter(p => selectedProfilesToSync.includes(p._id));
                        console.log(`🔍 [Check Selected] Checking ${selectedProfiles.length} selected profiles`);
                        batchLoadTrackCounts(selectedProfiles, 20);
                      }}
                      disabled={selectedProfilesToSync.length === 0 || isBatchLoading}
                      loading={isBatchLoading}
                      style={{ marginRight: 8 }}
                    >
                      Check Selected ({selectedProfilesToSync.length})
                    </Button>
                    <span style={{ marginLeft: 16 }}>
                      Selected: {selectedProfilesToSync.length} / {profilesForSync.length}
                    </span>
                  </div>
                  
                  <div style={{ maxHeight: 400, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 4 }}>
                    {profilesForSync
                      .sort((a, b) => {
                        // Sort by new track count (descending)
                        const aNewCount = profileTrackCounts[a._id]?.new || 0;
                        const bNewCount = profileTrackCounts[b._id]?.new || 0;
                        return bNewCount - aNewCount;
                      })
                      .map(profile => {
                      const { cityName } = getCityInfo(profile);
                      const displayCityName = cityName || 'Unknown';
                      
                      const isSelected = selectedProfilesToSync.includes(profile._id);
                      
                      return (
                        <div 
                          key={profile._id}
                          style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            padding: 12, 
                            borderBottom: '1px solid #f5f5f5',
                            cursor: 'pointer',
                            backgroundColor: isSelected ? '#f0f8ff' : 'white'
                          }}
                          onClick={() => {
                            if (isSelected) {
                              setSelectedProfilesToSync(prev => prev.filter(id => id !== profile._id));
                            } else {
                              setSelectedProfilesToSync(prev => [...prev, profile._id]);
                            }
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            style={{ marginRight: 12 }}
                            onChange={() => {}}
                          />
                          <Avatar src={profile.picture} size={32} style={{ marginRight: 12 }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 500 }}>{profile.username}</div>
                            <div style={{ fontSize: 12, color: '#666' }}>
                              iTunes: {profile.itunesMusicID} • City: {displayCityName}
                            </div>
                            {profileTrackCounts[profile._id] && (
                              <div style={{ fontSize: 11, marginTop: 2 }}>
                                <span style={{ color: '#52c41a' }}>
                                  {profileTrackCounts[profile._id].existing} existing
                                </span>
                                {' • '}
                                <span style={{ 
                                  color: profileTrackCounts[profile._id].new > 0 ? '#1890ff' : '#999',
                                  fontWeight: profileTrackCounts[profile._id].new > 0 ? 500 : 400
                                }}>
                                  {profileTrackCounts[profile._id].new} new tracks
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  <div style={{ textAlign: 'right', marginTop: 24 }}>
                    <Button onClick={() => setSyncOptionsModalVisible(false)} style={{ marginRight: 8 }}>
                      Cancel
                    </Button>
                    <Button 
                      type="primary" 
                      onClick={handleSelectiveSync}
                      disabled={selectedProfilesToSync.length === 0}
                    >
                      Sync Selected ({selectedProfilesToSync.length} profiles)
                    </Button>
                  </div>
                </div>
              )}

              {/* Sync by City Mode */}
              {profileSyncMode === 'byCity' && (
                <div>
                  <div style={{ marginBottom: 24 }}>
                    <p style={{ marginBottom: 16 }}>Select a city and choose which profiles to sync:</p>
                    <Select
                      placeholder="Choose a city..."
                      value={selectedSyncCity}
                      onChange={(cityId) => {
                        setSelectedSyncCity(cityId);
                        setSelectedCityProfiles([]); // Reset selection when city changes
                      }}
                      style={{ width: '100%' }}
                      size="large"
                    >
                      {availableCities.map(city => {
                        const profileCount = profilesForSync.filter(p => {
                          const { cityId: profileCityId } = getCityInfo(p);
                          return profileCityId === city._id;
                        }).length;
                        
                        // Try to show proper name, but fallback to ID if that's all we have
                        const displayName = (city.name && city.name.trim() !== '' && city.name !== city._id) 
                          ? city.name 
                          : city.name || city._id || 'Unknown City';
                        
                        return (
                          <Select.Option key={city._id} value={city._id}>
                            {displayName} ({profileCount} profiles)
                          </Select.Option>
                        );
                      })}
                    </Select>
                  </div>
                  
                  {selectedSyncCity && (() => {
                    const cityProfiles = profilesForSync.filter(p => {
                      const { cityId: profileCityId } = getCityInfo(p);
                      return profileCityId === selectedSyncCity;
                    });
                    
                    const cityName = availableCities.find(c => c._id === selectedSyncCity)?.name || 'Unknown';
                    
                    return (
                      <div>
                        {/* Selection Summary */}
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          marginBottom: 16,
                          padding: 12,
                          backgroundColor: '#f6f6f6',
                          borderRadius: 4
                        }}>
                          <div>
                            <Text strong>{selectedCityProfiles.length} of {cityProfiles.length}</Text> profiles selected from <Text strong>{cityName}</Text>
                          </div>
                          <div>
                            <Button 
                              size="small" 
                              onClick={() => setSelectedCityProfiles(cityProfiles.map(p => p._id))}
                              disabled={selectedCityProfiles.length === cityProfiles.length}
                              style={{ marginRight: 8 }}
                            >
                              Select All
                            </Button>
                            <Button 
                              size="small" 
                              onClick={() => setSelectedCityProfiles([])}
                              disabled={selectedCityProfiles.length === 0}
                            >
                              Deselect All
                            </Button>
                          </div>
                        </div>
                        
                        {/* Profile List with Checkboxes */}
                        <div style={{ maxHeight: 400, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 4 }}>
                          {cityProfiles.map(profile => {
                            const isSelected = selectedCityProfiles.includes(profile._id);
                            const trackCounts = profileTrackCounts[profile._id];
                            
                            return (
                              <div 
                                key={profile._id}
                                style={{ 
                                  display: 'flex', 
                                  alignItems: 'center',
                                  padding: 12,
                                  borderBottom: '1px solid #f5f5f5',
                                  backgroundColor: isSelected ? '#e6f7ff' : 'transparent'
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedCityProfiles(prev => [...prev, profile._id]);
                                    } else {
                                      setSelectedCityProfiles(prev => prev.filter(id => id !== profile._id));
                                    }
                                  }}
                                  style={{ marginRight: 12 }}
                                />
                                
                                <Avatar 
                                  src={profile.picture} 
                                  size={40} 
                                  style={{ marginRight: 12 }} 
                                />
                                
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontWeight: 500, marginBottom: 4 }}>
                                    {profile.username}
                                  </div>
                                  <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                                    iTunes: {profile.itunesMusicID}
                                  </div>
                                  {trackCounts ? (
                                    <div style={{ fontSize: 11, color: '#52c41a' }}>
                                      {trackCounts.existing} existing • {trackCounts.new} new tracks
                                    </div>
                                  ) : (
                                    <div style={{ fontSize: 11, color: '#999' }}>
                                      Track counts not loaded
                                    </div>
                                  )}
                                </div>
                                
                                <Button
                                  size="small"
                                  onClick={() => previewProfileTracks(profile)}
                                  loading={trackPreviewLoading && trackPreviewData?.profile?._id === profile._id}
                                >
                                  Preview Tracks
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                  
                  <div style={{ textAlign: 'right', marginTop: 24 }}>
                    <Button onClick={() => setSyncOptionsModalVisible(false)} style={{ marginRight: 8 }}>
                      Cancel
                    </Button>
                    <Button 
                      type="primary" 
                      onClick={() => {
                        if (selectedCityProfiles.length === 0) {
                          message.warning('Please select at least one profile to sync');
                          return;
                        }
                        
                        console.log('🔄 [CitySync] Selected profiles for sync:', selectedCityProfiles);
                        console.log('🔄 [CitySync] Available profiles in profilesForSync:', profilesForSync.length);
                        
                        // Update the selected profiles for sync and trigger sync
                        setSelectedProfilesToSync(selectedCityProfiles);
                        setSyncOptionsModalVisible(false);
                        // Start sync with selected profiles
                        handleSyncSelectedProfiles(selectedCityProfiles);
                      }}
                      disabled={!selectedSyncCity || selectedCityProfiles.length === 0}
                    >
                      Sync Selected Profiles ({selectedCityProfiles.length})
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </Modal>

      {/* Sync Progress Modal (for both single and all) */}
      <Modal
        title={syncAllProcessing ? "Syncing All Profiles" : "Syncing Profile"}
        open={syncProcessing || syncAllProcessing}
        footer={[
          <Button 
            key="cancel" 
            onClick={() => setCancelSyncOperation(true)}
            disabled={cancelSyncOperation}
          >
            {cancelSyncOperation ? 'Cancelling...' : 'Cancel'}
          </Button>
        ]}
        closable={false}
        width={500}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Spin size="large" />
          <p style={{ marginTop: 16, fontSize: '16px' }}>
            {syncAllProcessing ? 
              'Syncing tracks for all profiles with iTunes IDs...' :
              `Syncing tracks for ${selectedProfile?.username}...`
            }
          </p>
          {syncAllProcessing && (
            <p style={{ color: '#666', fontSize: '14px' }}>
              Processing: {syncCurrentProfile}
            </p>
          )}
          <p style={{ color: '#666', fontSize: '14px' }}>
            Current track: {batchCurrentTrack}
          </p>
          <Progress
            percent={batchTotal > 0 ? Math.round((batchProgress / batchTotal) * 100) : 0}
            status="active"
            format={(percent) => `${batchProgress}/${batchTotal} (${percent}%)`}
            style={{ marginTop: 16 }}
          />
          {syncAllProcessing && (
            <Progress
              percent={syncTotal > 0 ? Math.round((syncProgress / syncTotal) * 100) : 0}
              status="active"
              format={(percent) => `Profile ${syncProgress}/${syncTotal} (${percent}%)`}
              style={{ marginTop: 8 }}
            />
          )}
          <p style={{ fontSize: '12px', color: '#999', marginTop: 16 }}>
            {cancelSyncOperation ? 
              'Cancelling operation... Current track will complete first.' :
              'Click Cancel to stop the sync operation.'
            }
          </p>
        </div>
      </Modal>

      {/* Track Preview Modal */}
      <Modal
        title={`Track Preview - ${trackPreviewData?.profile?.username || ''}`}
        open={trackPreviewVisible}
        onCancel={() => {
          setTrackPreviewVisible(false);
          setTrackPreviewData(null);
        }}
        width={800}
        footer={[
          <Button key="close" onClick={() => setTrackPreviewVisible(false)}>
            Close
          </Button>
        ]}
      >
        {trackPreviewData && (
          <div>
            {trackPreviewData.loading ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Spin size="large" />
                <p style={{ marginTop: 16 }}>Loading track preview...</p>
              </div>
            ) : trackPreviewData.error ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <p style={{ color: '#ff4d4f' }}>Error loading tracks: {trackPreviewData.error}</p>
              </div>
            ) : (
              <div>
                {/* Summary */}
                <div style={{ 
                  marginBottom: 24, 
                  padding: 16, 
                  backgroundColor: '#f6f6f6', 
                  borderRadius: 8,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <Text strong style={{ fontSize: 16 }}>
                      {trackPreviewData.profile.username}
                    </Text>
                    <div style={{ marginTop: 4, color: '#666' }}>
                      iTunes ID: {trackPreviewData.profile.itunesMusicID}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: '#52c41a', fontWeight: 500 }}>
                      {trackPreviewData.existingTracks.length} existing tracks
                    </div>
                    <div style={{ color: '#1890ff', fontWeight: 500 }}>
                      {trackPreviewData.newTracks.length} new tracks available
                    </div>
                  </div>
                </div>

                {/* Existing Tracks */}
                {trackPreviewData.existingTracks.length > 0 && (
                  <div style={{ marginBottom: 24 }}>
                    <Text strong style={{ fontSize: 16, color: '#52c41a' }}>
                      Existing Tracks ({trackPreviewData.existingTracks.length})
                    </Text>
                    <div style={{ 
                      maxHeight: 200, 
                      overflowY: 'auto', 
                      border: '1px solid #d9d9d9', 
                      borderRadius: 4, 
                      marginTop: 8 
                    }}>
                      {trackPreviewData.existingTracks.slice(0, 10).map((track, index) => (
                        <div 
                          key={index}
                          style={{ 
                            padding: '8px 12px', 
                            borderBottom: index < Math.min(trackPreviewData.existingTracks.length - 1, 9) ? '1px solid #f0f0f0' : 'none'
                          }}
                        >
                          <div style={{ fontWeight: 500 }}>{track.title}</div>
                          <div style={{ fontSize: 12, color: '#666' }}>
                            {track.artist} • {track.releaseDate ? new Date(track.releaseDate).toLocaleDateString() : 'No date'}
                          </div>
                        </div>
                      ))}
                      {trackPreviewData.existingTracks.length > 10 && (
                        <div style={{ padding: '8px 12px', color: '#999', fontStyle: 'italic' }}>
                          ... and {trackPreviewData.existingTracks.length - 10} more tracks
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* New Tracks */}
                {trackPreviewData.newTracks.length > 0 ? (
                  <div>
                    <Text strong style={{ fontSize: 16, color: '#1890ff' }}>
                      New Tracks Available ({trackPreviewData.newTracks.length})
                    </Text>
                    <div style={{ 
                      maxHeight: 300, 
                      overflowY: 'auto', 
                      border: '1px solid #d9d9d9', 
                      borderRadius: 4, 
                      marginTop: 8 
                    }}>
                      {trackPreviewData.newTracks.map((track, index) => (
                        <div 
                          key={track.trackId || index}
                          style={{ 
                            display: 'flex',
                            alignItems: 'center',
                            padding: '8px 12px', 
                            borderBottom: index < trackPreviewData.newTracks.length - 1 ? '1px solid #f0f0f0' : 'none'
                          }}
                        >
                          {track.artworkUrl100 && (
                            <img 
                              src={track.artworkUrl100} 
                              alt={track.trackName}
                              style={{ 
                                width: 40, 
                                height: 40, 
                                borderRadius: 4, 
                                marginRight: 12,
                                objectFit: 'cover'
                              }} 
                            />
                          )}
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 500 }}>{track.trackName}</div>
                            <div style={{ fontSize: 12, color: '#666' }}>
                              {track.artistName} • {track.releaseDate ? new Date(track.releaseDate).toLocaleDateString() : 'No date'}
                            </div>
                          </div>
                          {track.previewUrl && (
                            <audio 
                              controls 
                              style={{ width: 150, height: 30 }}
                              src={track.previewUrl}
                            >
                              Your browser does not support the audio element.
                            </audio>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <Text style={{ color: '#999' }}>No new tracks found</Text>
                    <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
                      All iTunes tracks for this profile are already synced
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ArtistCatalog; 